/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import {
  Dialog,
  DialogContent,
  Collapse,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import useSWR from 'swr';
import { endpoints, dataFetcher, axios, baseUrl } from 'Api';
import { AppContext } from 'Context';
import { v4 as uuid } from 'uuid';
import { useNavigate } from 'react-router-dom';
import { Pathname } from 'Routes';
import {
  saveUploadInfo,
  saveUploadDetails,
  saveDummy,
  removeDummy,
} from 'store/store';
import { useDispatch, useSelector } from 'react-redux';
import ForNoImage from 'Assets/Placeholder.png';
import generatingThumbnail from 'Assets/generating-thumbnail.jpg';
import {
  ALLOWED_UPLOADS_COUNT, trialUploadGate, getTrialMaxFiles, getTrialMaxGB,
} from 'Commons';
import { isNewUIEnabled } from 'hooks/useNewUI';
import CreateCategoryPage from 'Pages/private/content-layout/CreateCategory';
import { uploadLargeFile } from '../../Utils/uploadWrapper';
import { buildThumbnailPayload, THUMBNAIL_MAX_BYTES } from '../../Utils/buildThumbnailPayload';
import { generateThumbnailsWithWasm } from '../../Utils/wasmThumbnail';
import uploadManager from '../../Utils/uploadManagerSecure';
import { getBucketName, generateS3Url } from '../../Api/awsConfig';
import { updateDraftLink } from '../../Api/uploaderApiFunctions';
import { UPLOAD_CONFIG } from '../../Config/uploadConfig';
import MultipleVideoUploadModalClassic from './MultipleVideoUploadModalClassic';

/* ===========================================================================
   Design system — V2 white theme.

   This modal used to render a dense MUI <Table> styled by the CSS module it
   SHARED with MultipleVideoEditModal. That module is deliberately no longer
   imported here: the redesign is self-contained (tokens + inline Lucide icons +
   one injected <style> scoped to `.mvu-root`), exactly like the already-
   modernised VideoEditModal, so nothing about the bulk-EDIT modal moves when
   this one changes.

   Tokens come from src/Pages/private/v2/_shared/theme.js — no new hex.
   =========================================================================== */
const V2 = {
  ink: '#15171c', // primary text + dark CTA
  sub: '#6b7280', // secondary text
  faint: '#9ca3af', // micro-labels
  slate: '#475569', // icon accent
  border: 'rgba(15,17,28,0.08)',
  bg: '#f7f8fa', // page/well surface
  iconBox: '#eef1f5', // slate-tinted icon chip
  warn: '#d97706',
  danger: '#dc2626',
};

// Lucide icons (MIT) inlined so they can be animated on hover without pulling
// in a dependency — the same approach the rest of the new UI uses.
const lucideProps = (size = 18) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
});
const IconFilm = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <rect width="18" height="18" x="3" y="3" rx="2" />
    <path d="M7 3v18" />
    <path d="M3 7.5h4" />
    <path d="M3 12h18" />
    <path d="M3 16.5h4" />
    <path d="M17 3v18" />
    <path d="M17 7.5h4" />
    <path d="M17 16.5h4" />
  </svg>
);
const IconPlus = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <path d="M5 12h14" />
    <path d="M12 5v14" />
  </svg>
);
const IconX = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);
const IconTrash = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <path d="M3 6h18" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);
const IconSliders = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <line x1="4" x2="4" y1="21" y2="14" />
    <line x1="4" x2="4" y1="10" y2="3" />
    <line x1="12" x2="12" y1="21" y2="12" />
    <line x1="12" x2="12" y1="8" y2="3" />
    <line x1="20" x2="20" y1="21" y2="16" />
    <line x1="20" x2="20" y1="12" y2="3" />
    <line x1="2" x2="6" y1="14" y2="14" />
    <line x1="10" x2="14" y1="8" y2="8" />
    <line x1="18" x2="22" y1="16" y2="16" />
  </svg>
);
const IconChevronDown = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <path d="m6 9 6 6 6-6" />
  </svg>
);
const IconChevronUp = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <path d="m18 15-6-6-6 6" />
  </svg>
);
const IconCheck = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <path d="M20 6 9 17l-5-5" />
  </svg>
);
const IconUpload = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" x2="12" y1="3" y2="15" />
  </svg>
);
const IconFolder = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
  </svg>
);
const IconFolderTree = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <path d="M20 10a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1h-2.5a1 1 0 0 1-.8-.4l-.9-1.2A1 1 0 0 0 15 3h-2a1 1 0 0 0-1 1v5a1 1 0 0 0 1 1Z" />
    <path d="M20 21a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1h-2.9a1 1 0 0 1-.88-.55l-.42-.85a1 1 0 0 0-.92-.6H13a1 1 0 0 0-1 1v5a1 1 0 0 0 1 1Z" />
    <path d="M3 3v2c0 1.1.9 2 2 2h3" />
    <path d="M3 3v13c0 1.1.9 2 2 2h3" />
  </svg>
);
const IconGlobe = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
    <path d="M2 12h20" />
  </svg>
);
const IconLock = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
const IconUsers = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const IconListMusic = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <path d="M21 15V6" />
    <path d="M18.5 18a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
    <path d="M12 12H3" />
    <path d="M16 6H3" />
    <path d="M12 18H3" />
  </svg>
);
const IconImage = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
    <circle cx="9" cy="9" r="2" />
    <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
  </svg>
);
const IconType = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <polyline points="4 7 4 4 20 4 20 7" />
    <line x1="9" x2="15" y1="20" y2="20" />
    <line x1="12" x2="12" y1="4" y2="20" />
  </svg>
);
const IconAlignLeft = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <line x1="21" x2="3" y1="6" y2="6" />
    <line x1="15" x2="3" y1="12" y2="12" />
    <line x1="17" x2="3" y1="18" y2="18" />
  </svg>
);
const IconRotate = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
  </svg>
);
const IconAlert = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
  </svg>
);
const IconHardDrive = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <line x1="22" x2="2" y1="12" y2="12" />
    <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    <line x1="6" x2="6.01" y1="16" y2="16" />
    <line x1="10" x2="10.01" y1="16" y2="16" />
  </svg>
);
const IconSparkles = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
  </svg>
);

// Each icon plays its own signature motion on hover, in the spirit of
// lucide-animated.com — matching the treatment used on the new dashboard pages.
const SPRING = 'cubic-bezier(0.34,1.56,0.64,1)';
const MVU_STYLES = `
  .mvu-root { font-family: Montserrat, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
  .mvu-root *, .mvu-root *::before, .mvu-root *::after { box-sizing: border-box; }
  .mvu-root :focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(15,17,28,0.16); }

  /* ---- shell -------------------------------------------------------------- */
  .mvu-shell { display: flex; flex: 1 1 auto; flex-direction: column; min-height: 0; background: ${V2.bg}; }

  /* ---- header ------------------------------------------------------------- */
  .mvu-header { display: flex; align-items: center; gap: 16px; flex: none; padding: 18px 24px; background: #fff; border-bottom: 1px solid ${V2.border}; }
  .mvu-header-icon { flex: none; width: 42px; height: 42px; border-radius: 13px; display: grid; place-items: center; color: #f5f5f7; background: linear-gradient(180deg, #2a2d36 0%, #15171c 100%); box-shadow: 0 4px 12px rgba(15,17,28,0.22); }
  .mvu-header-text { flex: 1 1 auto; min-width: 0; }
  .mvu-title { margin: 0; font-size: 17px; font-weight: 700; letter-spacing: -0.015em; color: ${V2.ink}; }
  .mvu-subtitle { margin: 3px 0 0; display: flex; flex-wrap: wrap; align-items: center; gap: 7px; font-size: 12.5px; font-weight: 500; color: ${V2.sub}; }
  .mvu-subtitle strong { font-weight: 700; color: ${V2.ink}; }
  .mvu-dot { width: 3px; height: 3px; border-radius: 50%; background: rgba(15,17,28,0.28); }
  .mvu-header-actions { flex: none; display: flex; align-items: center; gap: 8px; }

  /* ---- buttons ------------------------------------------------------------ */
  .mvu-btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; height: 38px; padding: 0 17px; border: 1px solid transparent; border-radius: 999px; background: none; color: ${V2.ink}; font-family: inherit; font-size: 13px; font-weight: 600; letter-spacing: -0.005em; white-space: nowrap; cursor: pointer; transition: transform .18s ${SPRING}, background-color .16s ease, border-color .16s ease, box-shadow .18s ease, color .16s ease; }
  .mvu-btn:disabled { cursor: not-allowed; opacity: 0.45; }
  .mvu-btn:not(:disabled):hover { transform: translateY(-1px); }
  .mvu-btn:not(:disabled):active { transform: translateY(0); }
  .mvu-btn-sm { height: 32px; padding: 0 13px; font-size: 12px; gap: 6px; }
  .mvu-btn-ghost { background: #fff; border-color: rgba(15,17,28,0.16); }
  .mvu-btn-ghost:not(:disabled):hover { border-color: ${V2.ink}; background: rgba(21,23,28,0.04); }
  .mvu-btn-quiet { color: ${V2.sub}; }
  .mvu-btn-quiet:not(:disabled):hover { color: ${V2.ink}; background: rgba(21,23,28,0.05); }
  .mvu-btn-primary { color: #f5f5f7; background: linear-gradient(180deg, #2a2d36 0%, #15171c 100%); box-shadow: 0 8px 20px -8px rgba(15,17,28,0.55); }
  .mvu-btn-primary:not(:disabled):hover { background: linear-gradient(180deg, #34373f 0%, #1d1f25 100%); }
  .mvu-btn-primary:disabled { opacity: 1; color: #fff; background: #d5d8de; box-shadow: none; }
  .mvu-btn-danger { color: ${V2.danger}; background: #fff; border-color: rgba(220,38,38,0.45); }
  .mvu-btn-danger:not(:disabled):hover { border-color: ${V2.danger}; background: rgba(220,38,38,0.05); }

  .mvu-iconbtn { display: grid; place-items: center; width: 36px; height: 36px; padding: 0; border: 1px solid transparent; border-radius: 12px; background: none; color: ${V2.sub}; cursor: pointer; transition: transform .18s ${SPRING}, background-color .16s ease, color .16s ease; }
  .mvu-iconbtn:not(:disabled):hover { color: ${V2.ink}; background: rgba(21,23,28,0.06); }
  .mvu-iconbtn:disabled { cursor: not-allowed; opacity: 0.45; }
  .mvu-iconbtn-danger:not(:disabled):hover { color: ${V2.danger}; background: rgba(220,38,38,0.08); }

  /* ---- bulk-action toolbar ------------------------------------------------ */
  .mvu-toolbar { flex: none; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; padding: 11px 24px; background: #fff; border-bottom: 1px solid ${V2.border}; }
  .mvu-toolbar-label { margin-right: 4px; font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.8px; color: ${V2.faint}; }
  .mvu-toolbar-sep { width: 1px; height: 20px; margin: 0 4px; background: ${V2.border}; }
  .mvu-spacer { flex: 1 1 auto; }

  /* ---- chips -------------------------------------------------------------- */
  .mvu-chip { display: inline-flex; align-items: center; gap: 7px; height: 28px; padding: 0 11px; border: 1px solid ${V2.border}; border-radius: 999px; background: #f1f3f7; color: ${V2.slate}; font-size: 11.5px; font-weight: 600; white-space: nowrap; }

  /* ---- body --------------------------------------------------------------- */
  .mvu-body { flex: 1 1 auto; min-height: 0; overflow-y: auto; overflow-x: hidden; padding: 18px 24px 24px; display: flex; flex-direction: column; gap: 14px; }
  .mvu-body::-webkit-scrollbar { width: 10px; }
  .mvu-body::-webkit-scrollbar-thumb { border: 3px solid transparent; background-clip: content-box; background-color: rgba(15,17,28,0.18); border-radius: 99px; }
  .mvu-body::-webkit-scrollbar-thumb:hover { background-color: rgba(15,17,28,0.3); }

  /* ---- video card --------------------------------------------------------- */
  .mvu-card { position: relative; flex: none; overflow: hidden; background: #fff; border: 1px solid ${V2.border}; border-radius: 18px; box-shadow: 0 1px 2px rgba(15,17,28,0.04); transition: box-shadow .22s ease, border-color .22s ease; }
  .mvu-card:hover { border-color: rgba(15,17,28,0.13); box-shadow: 0 10px 28px -14px rgba(15,17,28,0.22); }
  .mvu-card-warn { border-color: rgba(217,119,6,0.38); }
  .mvu-card-warn::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 3px; background: ${V2.warn}; }
  .mvu-card-main { display: flex; gap: 18px; padding: 16px; }

  /* ---- thumbnail ---------------------------------------------------------- */
  .mvu-thumb { position: relative; flex: none; align-self: stretch; width: 200px; height: auto; min-height: 113px; overflow: hidden; border: 1px solid rgba(15,17,28,0.06); border-radius: 13px; background: ${V2.iconBox}; }
  .mvu-thumb img { position: absolute; inset: 0; display: block; width: 100%; height: 100%; object-fit: cover; }
  .mvu-thumb-veil { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 7px; background: rgba(15,17,28,0.64); color: #fff; -webkit-backdrop-filter: blur(2px); backdrop-filter: blur(2px); }
  .mvu-thumb-veil span { font-size: 10.5px; font-weight: 700; letter-spacing: 0.4px; }
  .mvu-thumb-index { position: absolute; left: 8px; top: 8px; display: grid; place-items: center; min-width: 22px; height: 22px; padding: 0 6px; border-radius: 7px; background: rgba(255,255,255,0.94); color: ${V2.ink}; font-size: 11px; font-weight: 800; }
  .mvu-thumb-dur { position: absolute; right: 8px; bottom: 8px; padding: 3px 7px; border-radius: 7px; background: rgba(15,17,28,0.78); color: #fff; font-size: 10.5px; font-weight: 700; font-variant-numeric: tabular-nums; }

  /* ---- fields ------------------------------------------------------------- */
  .mvu-fields { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; gap: 12px; }
  .mvu-label { display: block; margin-bottom: 6px; font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.7px; color: ${V2.faint}; }
  .mvu-row { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 14px; align-items: start; }
  .mvu-filename { display: block; margin-top: 6px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 11px; font-weight: 500; color: ${V2.faint}; }

  /* segmented visibility control — replaces the old click-to-cycle chip, so all
     three states are visible and reachable in one tap */
  .mvu-seg { display: inline-flex; gap: 2px; padding: 3px; border: 1px solid ${V2.border}; border-radius: 12px; background: #f1f3f7; }
  .mvu-seg button { display: inline-flex; align-items: center; gap: 6px; height: 30px; padding: 0 11px; border: none; border-radius: 9px; background: none; color: ${V2.sub}; font-family: inherit; font-size: 12px; font-weight: 600; cursor: pointer; transition: background-color .16s ease, color .16s ease, box-shadow .16s ease; }
  .mvu-seg button:hover { color: ${V2.ink}; }
  .mvu-seg button[aria-pressed='true'] { background: #fff; color: ${V2.ink}; box-shadow: 0 1px 3px rgba(15,17,28,0.14); }

  /* ---- card footer -------------------------------------------------------- */
  .mvu-card-foot { display: flex; align-items: center; gap: 10px; padding: 0 16px 14px; }
  .mvu-meta { display: inline-flex; align-items: center; gap: 6px; font-size: 11.5px; font-weight: 600; color: ${V2.sub}; font-variant-numeric: tabular-nums; }

  /* ---- expanded panel ----------------------------------------------------- */
  .mvu-more { display: flex; flex-direction: column; gap: 18px; margin: 0 16px 16px; padding: 16px; border: 1px solid rgba(15,17,28,0.06); border-radius: 14px; background: ${V2.bg}; }
  .mvu-more-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 13px; }
  .mvu-sect-head { display: flex; align-items: center; gap: 9px; margin-bottom: 11px; }
  .mvu-sect-icon { display: grid; place-items: center; flex: none; width: 27px; height: 27px; border-radius: 9px; background: ${V2.iconBox}; color: ${V2.slate}; }
  .mvu-sect-title { font-size: 12.5px; font-weight: 700; letter-spacing: -0.005em; color: ${V2.ink}; }

  /* thumbnail picker */
  .mvu-strip { display: flex; gap: 10px; overflow-x: auto; padding-bottom: 5px; }
  .mvu-strip::-webkit-scrollbar { height: 6px; }
  .mvu-strip::-webkit-scrollbar-thumb { background: rgba(15,17,28,0.16); border-radius: 99px; }
  .mvu-thumbopt { position: relative; flex: none; width: 106px; height: 60px; padding: 0; overflow: hidden; border: 2px solid transparent; border-radius: 11px; background: ${V2.iconBox}; cursor: pointer; transition: transform .18s ${SPRING}, border-color .16s ease, box-shadow .16s ease; }
  .mvu-thumbopt img { display: block; width: 100%; height: 100%; object-fit: cover; }
  .mvu-thumbopt:hover { transform: translateY(-2px); box-shadow: 0 6px 16px -8px rgba(15,17,28,0.4); }
  .mvu-thumbopt[aria-pressed='true'] { border-color: ${V2.ink}; }
  .mvu-thumbopt-check { position: absolute; right: 5px; top: 5px; display: grid; place-items: center; width: 18px; height: 18px; border-radius: 50%; background: ${V2.ink}; color: #fff; }

  /* group pickers */
  .mvu-groups { display: flex; flex-wrap: wrap; gap: 8px; }
  .mvu-groupchip { display: inline-flex; align-items: center; gap: 7px; height: 34px; padding: 0 13px; border: 1px solid rgba(15,17,28,0.14); border-radius: 999px; background: #fff; color: ${V2.slate}; font-family: inherit; font-size: 12.5px; font-weight: 600; cursor: pointer; transition: transform .18s ${SPRING}, background-color .16s ease, border-color .16s ease, color .16s ease; }
  .mvu-groupchip:hover { transform: translateY(-1px); border-color: ${V2.ink}; color: ${V2.ink}; }
  .mvu-groupchip[aria-pressed='true'] { border-color: ${V2.ink}; background: linear-gradient(180deg, #2a2d36 0%, #15171c 100%); color: #f5f5f7; }

  /* ---- notices ------------------------------------------------------------ */
  .mvu-note { display: flex; align-items: flex-start; gap: 9px; padding: 11px 13px; border: 1px solid rgba(217,119,6,0.28); border-radius: 12px; background: #fffbeb; color: #92400e; font-size: 12px; font-weight: 600; line-height: 1.45; }
  .mvu-banner { display: flex; align-items: flex-start; gap: 13px; flex: none; margin: 16px 24px 0; padding: 15px 17px; border: 1px solid rgba(220,38,38,0.22); border-radius: 16px; background: #fef2f2; }
  .mvu-banner-icon { display: grid; place-items: center; flex: none; width: 34px; height: 34px; border-radius: 11px; background: rgba(220,38,38,0.1); color: ${V2.danger}; }
  .mvu-banner h4 { margin: 0 0 3px; font-size: 13.5px; font-weight: 700; color: #991b1b; }
  .mvu-banner p { margin: 0; font-size: 12.5px; font-weight: 500; line-height: 1.5; color: #b91c1c; }

  /* ---- empty / loading states -------------------------------------------- */
  .mvu-state { display: flex; flex: 1 1 auto; flex-direction: column; align-items: center; justify-content: center; gap: 9px; padding: 56px 24px; text-align: center; }
  .mvu-state-icon { display: grid; place-items: center; width: 66px; height: 66px; border: 1px solid ${V2.border}; border-radius: 20px; background: #fff; color: #94a3b8; box-shadow: 0 1px 2px rgba(15,17,28,0.04); }
  .mvu-state-title { margin: 8px 0 0; font-size: 15.5px; font-weight: 700; letter-spacing: -0.01em; color: ${V2.ink}; }
  .mvu-state-sub { margin: 0; max-width: 360px; font-size: 12.5px; font-weight: 500; line-height: 1.55; color: ${V2.sub}; }

  /* skeleton — a shaped preview of the real cards instead of a bare spinner */
  .mvu-skel-card { display: flex; flex: none; gap: 18px; padding: 16px; border: 1px solid ${V2.border}; border-radius: 18px; background: #fff; }
  .mvu-skel-fields { display: flex; flex: 1 1 auto; flex-direction: column; gap: 11px; min-width: 0; }
  .mvu-skel { position: relative; overflow: hidden; border-radius: 9px; background: ${V2.iconBox}; }
  .mvu-skel::after { content: ''; position: absolute; inset: 0; transform: translateX(-100%); background: linear-gradient(90deg, transparent, rgba(255,255,255,0.78), transparent); animation: mvuShimmer 1.4s ease-in-out infinite; }
  @keyframes mvuShimmer { 100% { transform: translateX(100%); } }

  .mvu-spin { flex: none; width: 16px; height: 16px; border: 2px solid currentColor; border-top-color: transparent; border-radius: 50%; animation: mvuSpin .7s linear infinite; }
  .mvu-spin-lg { width: 26px; height: 26px; border-width: 2.5px; }
  @keyframes mvuSpin { to { transform: rotate(360deg); } }

  /* ---- footer ------------------------------------------------------------- */
  .mvu-footer { flex: none; display: flex; align-items: center; gap: 14px; padding: 14px 24px; background: #fff; border-top: 1px solid ${V2.border}; }
  .mvu-footer-info { flex: 1 1 auto; min-width: 0; }
  .mvu-footer-count { display: flex; align-items: center; gap: 8px; font-size: 13.5px; font-weight: 700; letter-spacing: -0.01em; color: ${V2.ink}; }
  .mvu-footer-hint { display: flex; align-items: center; gap: 6px; margin: 3px 0 0; font-size: 11.5px; font-weight: 600; color: ${V2.warn}; }
  .mvu-footer-actions { flex: none; display: flex; align-items: center; gap: 10px; }

  /* ---- nested confirm dialogs -------------------------------------------- */
  .mvu-sheet { padding: 22px; }
  .mvu-sheet-head { display: flex; align-items: center; gap: 13px; margin-bottom: 18px; }
  .mvu-sheet-icon { display: grid; place-items: center; flex: none; width: 38px; height: 38px; border-radius: 12px; color: #f5f5f7; background: linear-gradient(180deg, #2a2d36 0%, #15171c 100%); }
  .mvu-sheet-title { margin: 0; font-size: 15.5px; font-weight: 700; letter-spacing: -0.015em; color: ${V2.ink}; }
  .mvu-sheet-sub { margin: 2px 0 0; font-size: 12px; font-weight: 500; color: ${V2.sub}; }
  .mvu-sheet-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 20px; }
  .mvu-optcards { display: flex; flex-direction: column; gap: 9px; }
  .mvu-optcard { display: flex; align-items: center; gap: 12px; width: 100%; padding: 13px 15px; border: 1.5px solid ${V2.border}; border-radius: 14px; background: #fff; color: ${V2.ink}; font-family: inherit; text-align: left; cursor: pointer; transition: transform .18s ${SPRING}, border-color .16s ease, background-color .16s ease, box-shadow .16s ease; }
  .mvu-optcard:hover { transform: translateY(-1px); border-color: rgba(15,17,28,0.2); box-shadow: 0 8px 20px -14px rgba(15,17,28,0.4); }
  .mvu-optcard[aria-pressed='true'] { border-color: ${V2.ink}; background: rgba(21,23,28,0.03); }
  .mvu-optcard-icon { display: grid; place-items: center; flex: none; width: 34px; height: 34px; border-radius: 11px; background: ${V2.iconBox}; color: ${V2.slate}; }
  .mvu-optcard-name { display: block; font-size: 13.5px; font-weight: 700; }
  .mvu-optcard-desc { display: block; margin-top: 1px; font-size: 11.5px; font-weight: 500; color: ${V2.sub}; }
  .mvu-optcard-tick { display: grid; place-items: center; flex: none; width: 22px; height: 22px; border-radius: 50%; background: ${V2.ink}; color: #fff; }

  /* ---- MUI field + menu overrides ---------------------------------------- */
  .mvu-root .MuiOutlinedInput-root { border-radius: 12px; background: #fff; font-family: Montserrat; transition: box-shadow .18s ease; }
  .mvu-root .MuiOutlinedInput-input { padding: 10px 13px; font-size: 13.5px; color: ${V2.ink}; }
  .mvu-root .MuiSelect-select { display: flex; align-items: center; gap: 8px; }
  .mvu-root .MuiOutlinedInput-notchedOutline { border-color: rgba(15,17,28,0.10); transition: border-color .15s ease; }
  .mvu-root .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline { border-color: rgba(15,17,28,0.20); }
  .mvu-root .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline { border-color: ${V2.ink}; border-width: 1.5px; }
  .mvu-root .MuiOutlinedInput-root.Mui-focused { box-shadow: 0 0 0 3px rgba(15,17,28,0.08); }
  .mvu-root .MuiSelect-icon { color: ${V2.sub}; }
  .mvu-root .MuiInputBase-input::placeholder { color: ${V2.faint}; opacity: 1; }
  .mvu-titlefield .MuiOutlinedInput-input { font-size: 14.5px; font-weight: 600; letter-spacing: -0.01em; }

  .mvu-menu { margin-top: 8px; border: 1px solid rgba(15,17,28,0.06); border-radius: 14px !important; box-shadow: 0 14px 40px rgba(15,17,28,0.16), 0 2px 8px rgba(15,17,28,0.06) !important; }
  .mvu-menu .MuiList-root { padding: 6px; }
  .mvu-menu .MuiMenuItem-root { margin: 2px 4px; padding: 8px 10px; border-radius: 10px; font-family: Montserrat; font-size: 13px; transition: background-color .15s ease; }
  .mvu-menu .MuiMenuItem-root:hover { background-color: #f2f4f7; }
  .mvu-menu .MuiMenuItem-root.Mui-selected { background-color: ${V2.iconBox}; }
  .mvu-menu .MuiMenuItem-root.Mui-selected:hover { background-color: #e6eaf0; }
  .mvu-menu .MuiDivider-root { margin: 5px 8px; background-color: rgba(15,17,28,0.07); }

  /* ---- icon motion ------------------------------------------------------- */
  .mvu-film { transition: transform .3s ${SPRING}; }
  .mvu-header-icon:hover .mvu-film { transform: scale(1.09); }
  .mvu-plus { transition: transform .25s ${SPRING}; }
  .mvu-btn:not(:disabled):hover .mvu-plus { transform: rotate(90deg); }
  .mvu-x { transition: transform .25s ${SPRING}; }
  .mvu-iconbtn:not(:disabled):hover .mvu-x { transform: rotate(90deg); }
  .mvu-trash { transform-origin: top center; transition: transform .25s ${SPRING}; }
  .mvu-iconbtn:not(:disabled):hover .mvu-trash { animation: mvuShake .5s ease; }
  @keyframes mvuShake { 0%, 100% { transform: rotate(0); } 25% { transform: rotate(-9deg); } 75% { transform: rotate(9deg); } }
  .mvu-sliders, .mvu-chev { transition: transform .25s ${SPRING}; }
  .mvu-btn:not(:disabled):hover .mvu-sliders { transform: scale(1.14); }
  .mvu-btn:not(:disabled):hover .mvu-chev { transform: translateY(2px); }
  .mvu-rotate { transition: transform .45s ${SPRING}; }
  .mvu-btn:not(:disabled):hover .mvu-rotate { transform: rotate(-180deg); }
  .mvu-uparrow { transition: transform .25s ${SPRING}; }
  .mvu-btn:not(:disabled):hover .mvu-uparrow { animation: mvuLift .65s ease infinite; }
  @keyframes mvuLift { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-2.5px); } }
  .mvu-sparkle { transition: transform .3s ${SPRING}; }
  .mvu-chip:hover .mvu-sparkle { transform: rotate(20deg) scale(1.12); }
  .mvu-tick path { stroke-dasharray: 24; stroke-dashoffset: 0; }
  .mvu-optcard:hover .mvu-tick path { animation: mvuDraw .5s ease; }
  @keyframes mvuDraw { from { stroke-dashoffset: 24; } to { stroke-dashoffset: 0; } }

  /* ---- responsive -------------------------------------------------------- */
  @media (max-width: 1024px) {
    .mvu-more-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  }
  @media (max-width: 780px) {
    .mvu-header { padding: 14px 16px; gap: 12px; }
    .mvu-toolbar { padding: 10px 16px; }
    .mvu-body { padding: 14px 16px 20px; }
    .mvu-banner { margin: 12px 16px 0; }
    .mvu-footer { padding: 12px 16px; flex-wrap: wrap; }
    .mvu-card-main { flex-direction: column; }
    .mvu-thumb { width: 100%; height: auto; min-height: 0; aspect-ratio: 16 / 9; }
    .mvu-row { grid-template-columns: minmax(0, 1fr); }
    .mvu-more-grid { grid-template-columns: minmax(0, 1fr); }
    .mvu-footer-actions { width: 100%; }
    .mvu-footer-actions .mvu-btn { flex: 1 1 auto; }
    .mvu-title { font-size: 15.5px; }
  }
  @media (prefers-reduced-motion: reduce) {
    .mvu-root *, .mvu-root *::after { animation-duration: .01ms !important; animation-iteration-count: 1 !important; transition-duration: .01ms !important; }
  }
`;

// Visibility is a first-class, three-way choice on this screen, so it gets a
// segmented control instead of the old click-to-cycle chip. One source of truth
// for the label/icon/blurb of each value.
const VISIBILITY_OPTIONS = [
  { value: '2', label: 'All Viewers', desc: 'Everyone who follows your projector', Icon: IconGlobe },
  { value: '1', label: 'Private', desc: 'Only you can see it', Icon: IconLock },
  { value: '3', label: 'Groups', desc: 'Only the groups you pick', Icon: IconUsers },
];

// MUI portals its menus to <body>, but the injected <style> is global, so the
// `.mvu-menu` class still reaches them.
const MENU_PROPS = {
  PaperProps: { className: 'mvu-menu', style: { maxHeight: 320 } },
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
  getContentAnchorEl: null,
  autoFocus: false,
  disableAutoFocusItem: true,
};

const MultipleVideoUploadModalNew = ({
  open,
  onClose,
  initialFiles = [],
  uploadLimitError = null,
  userData = null,
}) => {
  const {
    userDetails,
    setUploadContainer,
    setUploadContainer2,
    setIamUploading,
    draftCategoryState,
    setDraftCategoryState,
    base64ToBinary,
    setAppSnackbar,
    autoSignIn,
    setPopUpModalVisible,
    subscriptionDetails,
    freePlanLimitMb,
    IsfreePlan,
    totalOnGoingUploadSize,
    setShowAddPlaylistModal,
    playlistRefreshTrigger,
    newlyCreatedPlaylistId,
    setNewlyCreatedPlaylistId,
    setShowAddSubCategoryModal,
    subCategoryRefreshTrigger,
    newlyCreatedSubCategoryId,
    setNewlyCreatedSubCategoryId,
    navigateToInviteFirstTime,
    userAccessedData,
    isTrialPlan,
  } = useContext(AppContext);

  const userId = userDetails?.isSubscription?.user_id || localStorage.getItem('id');
  const navigate = useNavigate();

  // Redux for upload tracking
  const dispatch = useDispatch();
  const uploadPercentages = useSelector((state) => state.upload);
  const updateUploadPercentages = (draft_id, data) => {
    dispatch(saveUploadInfo({ [draft_id]: data }));
  };
  const updateUploadDetails = (draft_id, data) => {
    dispatch(saveUploadDetails({ [draft_id]: data }));
  };

  // State for all videos
  const [videos, setVideos] = useState([]);
  const [expandedVideos, setExpandedVideos] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [isLoadingVideos, setIsLoadingVideos] = useState(false);
  const [currentLimitError, setCurrentLimitError] = useState(null);
  const [thumbnailProgress, setThumbnailProgress] = useState({});
  const [isGeneratingThumbnails, setIsGeneratingThumbnails] = useState(false);
  const [pendingThumbnailGeneration, setPendingThumbnailGeneration] = useState(new Set());
  const [completedThumbnailGeneration, setCompletedThumbnailGeneration] = useState(new Set());

  // Ref for additional file input
  const additionalFileInputRef = useRef(null);

  // Toolbar dialog states
  const [applyCategoryDialogOpen, setApplyCategoryDialogOpen] = useState(false);
  const [selectedCategoryForAll, setSelectedCategoryForAll] = useState('');
  const [applyVisibilityDialogOpen, setApplyVisibilityDialogOpen] = useState(false);
  const [selectedVisibilityForAll, setSelectedVisibilityForAll] = useState('2');

  // Global data states
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupsLoaded, setGroupsLoaded] = useState(false);
  const [showCreateCategoryModal, setShowCreateCategoryModal] = useState(false);
  const [pendingVideoIdForCategory, setPendingVideoIdForCategory] = useState(null);
  const [pendingVideoIdForSubCategory, setPendingVideoIdForSubCategory] = useState(null);
  const [pendingVideoIdForPlaylist, setPendingVideoIdForPlaylist] = useState(null);
  const pendingVideoIdRef = useRef(null);

  // Track if we've applied default categories to prevent repeated updates
  const categoriesAppliedRef = useRef(false);
  // console.log('videos data>>>>>', videos);

  // Function to check and update upload limit error based on current video count
  const checkUploadLimit = useCallback(
    (currentVideoCount, updateErrorState = true, videosToCheck = []) => {
      // Trial accounts: a hard allowance of TRIAL_MAX_FILES files OR 5 GB, counted
      // as server content + anything in-flight + this batch. Checked first because
      // the free-plan block below only applies to no-subscription accounts, which a
      // trial is NOT (its subscription is non-null) — without this a trial could
      // upload without limit.
      if (isTrialPlan) {
        const currentlyUploadingCount = uploadManager.getCurrentlyUploadingCount();
        const existingCount = (userData?.totalContentUploaded || 0) + currentlyUploadingCount;
        const addBytes = (videosToCheck || []).reduce((sum, v) => sum + (v?.size || 0), 0);
        const usedMb = Number(
          subscriptionDetails?.storageUsed?.used_storage
          ?? subscriptionDetails?.storageUsed?.total_storage
          ?? 0,
        ) || 0;
        const gate = trialUploadGate({
          isTrial: true,
          existingCount,
          usedBytes: usedMb * 1024 * 1024,
          addCount: currentVideoCount,
          addBytes,
        });
        if (!gate.allowed) {
          const errorData = {
            currentCount: userData?.totalContentUploaded || 0,
            currentlyUploading: currentlyUploadingCount,
            tryingToUpload: currentVideoCount,
            maxAllowed: getTrialMaxFiles(),
            message: gate.reason === 'size'
              ? `Your free trial includes up to ${getTrialMaxGB()} GB of storage. Upgrade to a plan for unlimited storage.`
              : `Your free trial includes up to ${getTrialMaxFiles()} files. You can add ${gate.remainingFiles} more — upgrade for unlimited uploads.`,
          };
          if (updateErrorState) setCurrentLimitError(errorData);
          return errorData;
        }
        if (updateErrorState) setCurrentLimitError(null);
        return null;
      }
      // Check if user has data and no subscription
      if (userData && userData.subscription == null) {
        // Get currently uploading/pending items from upload manager
        const currentlyUploadingCount = uploadManager.getCurrentlyUploadingCount();

        // Check upload count limit
        const totalAfterCurrent = (userData.totalContentUploaded || 0)
          + currentlyUploadingCount
          + currentVideoCount;
        console.log('totalAfterCurrent', totalAfterCurrent);
        if (totalAfterCurrent > ALLOWED_UPLOADS_COUNT) {
          const errorData = {
            currentCount: userData.totalContentUploaded || 0,
            currentlyUploading: currentlyUploadingCount,
            tryingToUpload: currentVideoCount,
            maxAllowed: ALLOWED_UPLOADS_COUNT,
            message: `You can upload a maximum of ${ALLOWED_UPLOADS_COUNT} files on free plan. ${userData.totalContentUploaded === 0
              ? `You are trying to upload ${currentVideoCount}.`
              : `You can upload ${ALLOWED_UPLOADS_COUNT - (userData.totalContentUploaded + currentlyUploadingCount)
              } more files`
              }`,
          };

          if (updateErrorState) setCurrentLimitError(errorData);
          return errorData;
        }

        // Check file size limit for free plan users
        if (freePlanLimitMb && videosToCheck.length > 0) {
          const totalSizeMB = videosToCheck.reduce((sum, video) => sum + (video.size / 1024 / 1024), 0);
          const usedStorage = subscriptionDetails?.storageUsed?.total_storage;
          const allowedStorage = (usedStorage + totalOnGoingUploadSize) - freePlanLimitMb;
          const formattedStorage = (Math.abs(allowedStorage) / 1024).toFixed(2);
          const allFullStorage = totalSizeMB + usedStorage + totalOnGoingUploadSize;
          const exceededBy = allFullStorage - freePlanLimitMb;
          console.log('totalSizeMB:', allFullStorage, totalSizeMB, usedStorage, freePlanLimitMb, totalOnGoingUploadSize);
          if (IsfreePlan && allFullStorage > freePlanLimitMb) {
            const errorData = {
              totalSize: allFullStorage.toFixed(2),
              maxAllowed: freePlanLimitMb,
              message: `You can upload files up to ${Math.floor(freePlanLimitMb / 1024)} GB on the free plan. You can add ${formattedStorage} GB more.`,
            };
            if (updateErrorState) setCurrentLimitError(errorData);
            return errorData;
          }
        }
      }

      // No error - clear any existing error
      if (updateErrorState) setCurrentLimitError(null);
      return null;
    },
    [userData, uploadPercentages, freePlanLimitMb, isTrialPlan, subscriptionDetails]
  );

  // Check upload limit during video processing but don't show error yet
  useEffect(() => {
    if (isLoadingVideos && userData) {
      // Silently check upload limit while videos are loading so it's ready when loading completes
      checkUploadLimit(initialFiles.length);
    }
  }, [isLoadingVideos, initialFiles.length, checkUploadLimit, userData]);

  // Fetch categories
  const { isValidating: gettingCategories, mutate: getCategories } = useSWR(
    [endpoints.getMyCategories],
    {
      fetcher: (url) => dataFetcher(url),
      onSuccess: ({ success, data }) => {
        if (success && data) {
          setCategories(data);
          categoriesAppliedRef.current = false; // Reset flag when new categories are loaded
        }
      },
    }
  );

  // Fetch subcategories
  const { isValidating: gettingSubCategories, mutate: getSubCategories } = useSWR([endpoints.getMySubCategory], {
    fetcher: (url) => dataFetcher(url),
    onSuccess: ({ success, data }) => {
      if (success && data) {
        setSubCategories(data);
      }
    },
  });

  // Fetch playlists
  const { isValidating: gettingPlaylists, mutate: getPlaylists } = useSWR(
    [endpoints.getMyPlaylist],
    {
      fetcher: (url) => dataFetcher(url),
      onSuccess: ({ success, data }) => {
        if (success && data) {
          setPlaylists(data);
        }
      },
    }
  );

  // Fetch groups
  const { isValidating: gettingGroups, mutate: getGroups } = useSWR(
    [endpoints.getMyGroups],
    {
      fetcher: (url) => dataFetcher(url),
      onSuccess: ({ success, data, message }) => {
        if (success && data) {
          setGroups(data);
        } else if (!success && message === 'No Groups found') {
          setGroups([]);
        }
        setGroupsLoaded(true);
      },
      onError: (error) => {
        console.error('Groups API error:', error);
        setGroups([]);
        setGroupsLoaded(true);
      },
    }
  );

  // Manual API calls when modal opens
  useEffect(() => {
    const fetchAllData = async () => {
      if (!open) return;

      // Reset loaded states
      setGroupsLoaded(false);
      categoriesAppliedRef.current = false; // Reset category application flag

      try {
        // Fetch categories
        const categoriesResponse = await dataFetcher(endpoints.getMyCategories);
        if (categoriesResponse.success && categoriesResponse.data) {
          // Sort categories to always show "All Videos" at the top
          const sortedCategories = [...categoriesResponse.data].sort((a, b) => {
            const aIsAllVideos = a.title?.toLowerCase() === 'all videos';
            const bIsAllVideos = b.title?.toLowerCase() === 'all videos';
            if (aIsAllVideos && !bIsAllVideos) return -1;
            if (!aIsAllVideos && bIsAllVideos) return 1;
            return 0; // Keep original order for other categories
          });
          setCategories(sortedCategories);
        }

        // Fetch subcategories
        const subCategoriesResponse = await dataFetcher(
          endpoints.getMySubCategory
        );
        if (subCategoriesResponse.success && subCategoriesResponse.data) {
          setSubCategories(subCategoriesResponse.data);
        }

        // Fetch playlists
        const playlistsResponse = await dataFetcher(endpoints.getMyPlaylist);
        if (playlistsResponse.success && playlistsResponse.data) {
          setPlaylists(playlistsResponse.data);
        }

        // Fetch groups
        const groupsResponse = await dataFetcher(endpoints.getMyGroups);
        if (groupsResponse.success && groupsResponse.data) {
          setGroups(groupsResponse.data);
        } else if (
          !groupsResponse.success
          && groupsResponse.message === 'No Groups found'
        ) {
          setGroups([]);
        }
        setGroupsLoaded(true);
      } catch (error) {
        console.error('Error fetching data:', error);
        setGroupsLoaded(true);
      }
    };

    fetchAllData();
  }, [open]);

  // Refresh playlists when a new playlist is created from the global modal
  useEffect(() => {
    if (playlistRefreshTrigger > 0 && open) {
      const refreshPlaylists = async () => {
        try {
          const playlistsResponse = await dataFetcher(endpoints.getMyPlaylist);
          if (playlistsResponse.success && playlistsResponse.data) {
            setPlaylists(playlistsResponse.data);
            // Auto-select the newly created playlist only for the video that triggered the modal
            if (newlyCreatedPlaylistId && pendingVideoIdForPlaylist) {
              // Convert to number to match the type from API
              const newPlaylistId = typeof newlyCreatedPlaylistId === 'string'
                ? parseInt(newlyCreatedPlaylistId, 10)
                : newlyCreatedPlaylistId;
              setVideos((prevVideos) => prevVideos.map((video) => {
                if (video.id === pendingVideoIdForPlaylist) {
                  return {
                    ...video,
                    selectedPlaylist: newPlaylistId,
                  };
                }
                return video;
              }));
              setNewlyCreatedPlaylistId(null);
              setPendingVideoIdForPlaylist(null);
            }
          }
        } catch (error) {
          console.error('Error refreshing playlists:', error);
        }
      };
      refreshPlaylists();
    }
  }, [playlistRefreshTrigger, open]);

  // Refresh subcategories when a new subcategory is created from the global modal
  useEffect(() => {
    if (subCategoryRefreshTrigger > 0 && open) {
      const refreshSubCategories = async () => {
        try {
          const subCategoriesResponse = await dataFetcher(endpoints.getMySubCategory);
          if (subCategoriesResponse.success && subCategoriesResponse.data) {
            setSubCategories(subCategoriesResponse.data);
            // Auto-select the newly created subcategory only for the video that triggered the modal
            if (newlyCreatedSubCategoryId && pendingVideoIdForSubCategory) {
              // Convert to number to match the type from API
              const newSubCategoryId = typeof newlyCreatedSubCategoryId === 'string'
                ? parseInt(newlyCreatedSubCategoryId, 10)
                : newlyCreatedSubCategoryId;
              setVideos((prevVideos) => prevVideos.map((video) => {
                if (video.id === pendingVideoIdForSubCategory) {
                  return {
                    ...video,
                    selectedSubCategory: newSubCategoryId,
                  };
                }
                return video;
              }));
              setNewlyCreatedSubCategoryId(null);
              setPendingVideoIdForSubCategory(null);
            }
          }
        } catch (error) {
          console.error('Error refreshing subcategories:', error);
        }
      };
      refreshSubCategories();
    }
  }, [subCategoryRefreshTrigger, open]);

  // Handle category creation callback - refresh list and select new category for the video
  const handleCategoryCreated = async () => {
    const videoId = pendingVideoIdRef.current;
    try {
      const categoriesResponse = await dataFetcher(endpoints.getMyCategories);
      if (categoriesResponse.success && categoriesResponse.data) {
        const newCategories = categoriesResponse.data;
        // Select the most recently created category (last in the list by id)
        const sortedCategories = [...newCategories].sort((a, b) => parseInt(b.id, 10) - parseInt(a.id, 10));
        const newestCategory = sortedCategories[0];

        // Sort categories to always show "All Videos" at the top for display
        const sortedCategoriesForDisplay = [...newCategories].sort((a, b) => {
          const aIsAllVideos = a.title?.toLowerCase() === 'all videos';
          const bIsAllVideos = b.title?.toLowerCase() === 'all videos';
          if (aIsAllVideos && !bIsAllVideos) return -1;
          if (!aIsAllVideos && bIsAllVideos) return 1;
          return 0; // Keep original order for other categories
        });

        // First update categories list so the new category is valid
        setCategories(sortedCategoriesForDisplay);

        // Then update the video with the new category
        if (newestCategory && videoId) {
          if (videoId === '__all__') {
            // Apply to all videos
            setVideos((prevVideos) => prevVideos.map((video) => ({
              ...video,
              selectedCategory: {
                value: newestCategory.id,
                name: newestCategory.title,
              },
            })));
          } else {
            // Update the specific video's category
            setVideos((prevVideos) => prevVideos.map((video) => {
              if (video.id === videoId) {
                return {
                  ...video,
                  selectedCategory: {
                    value: newestCategory.id,
                    name: newestCategory.title,
                  },
                };
              }
              return video;
            }));
          }
        }
      }
    } catch (error) {
      console.error('Error refreshing categories:', error);
    } finally {
      pendingVideoIdRef.current = null;
      setPendingVideoIdForCategory(null);
      setShowCreateCategoryModal(false);
    }
  };

  // Helper function to generate thumbnails
  const blobToBase64 = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

  // Backend thumbnail fallback — sends first 5MB of video to server for FFmpeg-based generation
  // Server enforces an upload size limit (full file → 413). Send the first 5MB
  // (enough for faststart MP4s), except for non-faststart files whose `moov` is
  // at the END — for those send a small trimmed payload carrying moov + early
  // frames so it stays under the limit yet is parseable (buildThumbnailPayload).
  // Resolves an ARRAY of thumbnail data URLs (server returns 1; wasm returns up
  // to 6 — same as the local flow), or null if nothing could be generated.
  const callBackendThumbnailAPI = (file) => new Promise((resolve) => {
    (async () => {
      // 1) Server (FFmpeg) with a small, parseable payload under the size limit.
      try {
        const trimmed = await buildThumbnailPayload(file);
        const piece = trimmed || file.slice(0, THUMBNAIL_MAX_BYTES);
        const videoFilePiece = new FormData();
        videoFilePiece.append('video_file', piece);
        const response = await fetch('https://uploader.projector.app/generate-thumbnail/', {
          method: 'POST',
          body: videoFilePiece,
        });
        if (response.ok) {
          const data = await response.json();
          if (data?.thumbnail) { resolve([`data:image/png;base64,${data.thumbnail}`]); return; }
        }
      } catch (e) { /* fall through to wasm */ }
      // 2) Last resort: client-side ffmpeg.wasm (lazy-loaded) → up to 6 frames.
      let wasmThumbs = [];
      try { wasmThumbs = await generateThumbnailsWithWasm(file, { count: 6 }); } catch (e) { wasmThumbs = []; }
      resolve(wasmThumbs.length ? wasmThumbs : null);
    })();
  });

  // Generate thumbnails at specific time positions
  const generateThumbnailAtTime = (file, timePosition) => new Promise((resolve) => {
    const video = document.createElement('video');
    const videoUrl = URL.createObjectURL(file);
    video.src = videoUrl;
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.playsInline = true;

    video.onerror = () => {
      URL.revokeObjectURL(videoUrl);
      video.remove();
      resolve(null);
    };

    video.onloadeddata = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      const maxWidth = 640;
      const scale = Math.min(1, maxWidth / video.videoWidth);
      canvas.width = video.videoWidth * scale;
      canvas.height = video.videoHeight * scale;

      video.currentTime = timePosition;

      video.onseeked = () => {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(
          async (blob) => {
            if (blob) {
              const base64 = await blobToBase64(blob);
              URL.revokeObjectURL(videoUrl);
              video.remove();
              canvas.remove();
              resolve(base64);
            } else {
              URL.revokeObjectURL(videoUrl);
              video.remove();
              canvas.remove();
              resolve(null);
            }
          },
          'image/jpeg',
          0.8
        );
      };
    };
  });

  const generateThumbnailsFromVideo = (file, thumbnailCount = 6) => new Promise((resolve) => {
    const video = document.createElement('video');
    const videoUrl = URL.createObjectURL(file);
    video.src = videoUrl;
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.playsInline = true;

    // Add error handling for thumbnail generation failure
    video.onerror = () => {
      URL.revokeObjectURL(videoUrl);
      video.remove();
      resolve({
        thumbnails: [], // Empty array indicates failure
        width: 0,
        height: 0,
        duration: 0,
        thumbnailFailed: true,
      });
    };

    video.onloadeddata = () => {
      const { duration } = video;
      const interval = duration / (thumbnailCount + 1);
      const tempThumbnails = [];

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      const maxWidth = 640;
      const scale = Math.min(1, maxWidth / video.videoWidth);
      canvas.width = video.videoWidth * scale;
      canvas.height = video.videoHeight * scale;

      let currentIndex = 1;

      const captureNextFrame = () => {
        if (currentIndex > thumbnailCount) {
          URL.revokeObjectURL(videoUrl);
          video.remove();
          canvas.remove();
          resolve({
            thumbnails: tempThumbnails,
            width: video.videoWidth,
            height: video.videoHeight,
            duration,
          });
          return;
        }

        const time = interval * currentIndex;
        video.currentTime = time;

        video.onseeked = () => {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

          canvas.toBlob(
            async (blob) => {
              if (blob) {
                const base64 = await blobToBase64(blob);
                tempThumbnails.push(base64);
              }
              currentIndex += 1;
              captureNextFrame();
            },
            'image/jpeg',
            0.8
          );
        };
      };

      captureNextFrame();
    };
  });

  // Generate remaining thumbnails for a video one by one with progress tracking
  const generateRemainingThumbnails = async (video, videoIndex, totalVideos) => {
    if (video.thumbnails.length >= 6 || video.thumbnailFailed) {
      return video; // Already has all thumbnails or failed
    }

    try {
      const remainingCount = 5; // Need 5 more thumbnails
      const allThumbnails = [...video.thumbnails];

      // Calculate evenly distributed time positions for all 6 thumbnails
      const { duration } = video;
      const totalThumbnails = 6;
      const interval = duration / (totalThumbnails + 1);

      // We already have the first thumbnail at position: interval * 1
      // Now generate thumbnails at positions 2, 3, 4, 5, 6
      const timePositions = [];
      for (let i = 2; i <= totalThumbnails; i += 1) {
        timePositions.push(interval * i);
      }

      // Generate thumbnails one by one using reduce to avoid await in loop
      await Array.from({ length: remainingCount }).reduce(async (promise, _, i) => {
        await promise; // Wait for previous iteration

        setThumbnailProgress((prev) => ({
          ...prev,
          [video.id]: {
            current: i + 1,
            total: remainingCount,
            videoIndex: videoIndex + 1,
            totalVideos,
          },
        }));

        // Generate thumbnail at specific time position
        const thumbnail = await generateThumbnailAtTime(video.file, timePositions[i]);

        if (thumbnail) {
          allThumbnails.push(thumbnail);

          // Update video with new thumbnail immediately
          setVideos((prevVideos) => prevVideos.map((v) => {
            if (v.id === video.id) {
              return {
                ...v,
                thumbnails: [...allThumbnails],
              };
            }
            return v;
          }));
        }
      }, Promise.resolve());

      // Clear progress for this video
      setThumbnailProgress((prev) => {
        const newProgress = { ...prev };
        delete newProgress[video.id];
        return newProgress;
      });

      // Mark as completed
      setCompletedThumbnailGeneration((prev) => {
        const newSet = new Set(prev);
        newSet.add(video.id);
        return newSet;
      });

      // Remove completed status after 2 seconds
      setTimeout(() => {
        setCompletedThumbnailGeneration((prev) => {
          const newSet = new Set(prev);
          newSet.delete(video.id);
          return newSet;
        });
      }, 2000);

      return {
        ...video,
        thumbnails: allThumbnails,
      };
    } catch (error) {
      console.error('Error generating additional thumbnails:', error);
      // Clear progress on error
      setThumbnailProgress((prev) => {
        const newProgress = { ...prev };
        delete newProgress[video.id];
        return newProgress;
      });
    }

    return video; // Return original if generation fails
  };

  // Initialize videos from files
  useEffect(() => {
    const processVideos = async () => {
      if (initialFiles.length > 0 && open) {
        setIsLoadingVideos(true);

        // Generate only 1 thumbnail initially for faster display
        const videoPromises = initialFiles.map(async (file) => {
          const id = uuid();
          const videoData = await generateThumbnailsFromVideo(file, 1); // Only 1 thumbnail
          const filename = file.name.replace(/\.[^/.]+$/, '');

          // Handle failed thumbnail generation — fallback to backend
          let thumbnails;
          let { thumbnailFailed } = videoData;
          if (videoData.thumbnails && videoData.thumbnails.length > 0) {
            thumbnails = videoData.thumbnails;
          } else {
            const backendThumbs = await callBackendThumbnailAPI(file);
            if (backendThumbs?.length) {
              thumbnails = backendThumbs;
              thumbnailFailed = false;
            } else {
              thumbnails = [ForNoImage];
            }
          }

          return {
            id,
            file,
            title: filename,
            description: '',
            visibility: '2', // Public
            selectedCategory:
              categories.length > 0
                ? { value: categories[0].id, name: categories[0].title }
                : { value: '', name: '' },
            selectedSubCategory: '',
            selectedPlaylist: '',
            selectedGroups: [],
            thumbnails,
            selectedThumbnail: 0,
            isGeneratingThumbnails: false,
            duration: videoData.duration || 0,
            size: file.size,
            width: videoData.width || 0,
            height: videoData.height || 0,
            thumbnailFailed: thumbnailFailed || false,
          };
        });

        const processedVideos = await Promise.all(videoPromises);
        setVideos(processedVideos);
        setIsLoadingVideos(false);

        // Generate remaining thumbnails in the background after list is shown
        setTimeout(async () => {
          setIsGeneratingThumbnails(true);

          // Mark all videos as pending
          setPendingThumbnailGeneration(new Set(processedVideos.map((v) => v.id)));

          // Generate thumbnails for each video sequentially to show progress using reduce
          await processedVideos.reduce(async (promise, video, i) => {
            await promise;

            // Remove from pending before starting generation
            setPendingThumbnailGeneration((prev) => {
              const newSet = new Set(prev);
              newSet.delete(video.id);
              return newSet;
            });

            await generateRemainingThumbnails(video, i, processedVideos.length);
          }, Promise.resolve());

          setIsGeneratingThumbnails(false);
          setPendingThumbnailGeneration(new Set());
        }, 100); // Small delay to ensure UI updates first
      }
    };

    processVideos();
  }, [initialFiles, open]); // eslint-disable-line

  // Update existing videos when categories are loaded
  useEffect(() => {
    if (
      categories.length > 0
      && videos.length > 0
      && !categoriesAppliedRef.current
    ) {
      const defaultCategory = categories[0]; // First category should be "All Videos"

      const needsUpdate = videos.some((video) => {
        const hasValidCategory = video.selectedCategory.value
          && categories.find((cat) => cat.id === video.selectedCategory.value);
        return !hasValidCategory;
      });

      if (needsUpdate) {
        setVideos((prevVideos) => prevVideos.map((video) => {
          const hasValidCategory = video.selectedCategory.value
            && categories.find((cat) => cat.id === video.selectedCategory.value);

          if (!hasValidCategory) {
            return {
              ...video,
              selectedCategory: {
                value: defaultCategory.id,
                name: defaultCategory.title,
              },
            };
          }
          return video;
        }));
        categoriesAppliedRef.current = true;
      }
    }
  }, [categories, videos]);

  // Initialize and update limit error when video count changes
  useEffect(() => {
    if (videos.length > 0 && !isLoadingVideos && userData) {
      checkUploadLimit(videos.length, true, videos);
    } else if (videos.length === 0) {
      setCurrentLimitError(null);
    }
  }, [videos.length, isLoadingVideos, checkUploadLimit, userData, videos]);

  // Initialize limit error when userData changes
  useEffect(() => {
    if (videos.length > 0 && userData) {
      checkUploadLimit(videos.length, true, videos);
    }
  }, [videos.length, checkUploadLimit, userData, videos]);

  const updateVideo = useCallback(
    (videoId, updates) => {
      setVideos((prevVideos) => prevVideos.map((video) => (video.id === videoId ? { ...video, ...updates } : video)));

      // Auto-expand if visibility is changed to Group but no groups selected
      if (updates.visibility === '3') {
        const video = videos.find((v) => v.id === videoId);
        if (!video?.selectedGroups || video.selectedGroups.length === 0) {
          setExpandedVideos((prev) => ({
            ...prev,
            [videoId]: true,
          }));
        }
      }
    },
    [videos]
  );

  const removeVideo = useCallback((videoId) => {
    setVideos((prevVideos) => {
      const videoToRemove = prevVideos.find((v) => v.id === videoId);
      if (videoToRemove?.thumbnails) {
        videoToRemove.thumbnails.forEach((url) => {
          if (url && url.startsWith('blob:')) {
            URL.revokeObjectURL(url);
          }
        });
      }
      return prevVideos.filter((video) => video.id !== videoId);
    });
  }, []);

  const toggleExpanded = useCallback((videoId) => {
    setExpandedVideos((prev) => ({
      ...prev,
      [videoId]: !prev[videoId],
    }));
  }, []);

  const handleAddMoreVideos = useCallback(
    async (event) => {
      const files = Array.from(event.target.files);
      // webm added for interview-recording uploads (single video may
      // promote to multi if user adds more after the recording).
      const allowedVideoTypes = [
        '.mp4',
        '.mov',
        '.mpg',
        '.m4v',
        '.m2ts',
        '.hevc',
        '.h264',
        '.webm',
      ];

      const videoFiles = [];
      const invalidFiles = [];

      files.forEach((file) => {
        const fileExtension = file.name
          .toLowerCase()
          .slice(file.name.lastIndexOf('.'));
        if (allowedVideoTypes.includes(fileExtension)) {
          videoFiles.push(file);
        } else {
          invalidFiles.push(file.name);
        }
      });

      if (invalidFiles.length > 0) {
        setAppSnackbar({
          isVisible: true,
          message: `Unsupported file format(s): ${invalidFiles.join(
            ', '
          )}. Please upload videos in MP4, MOV, MPG, M4V, M2TS, HEVC, or H264 format.`,
          type: 'error',
        });
      }

      if (videoFiles.length === 0) {
        return;
      }

      setIsLoadingVideos(true);

      // Pre-check upload limit silently during video processing (don't update error state yet)
      const currentVideoCount = videos.length;
      const totalVideosAfterAdding = currentVideoCount + videoFiles.length;

      // Silently check limit during processing using cached userData
      if (userData) {
        checkUploadLimit(totalVideosAfterAdding, false);
      }

      // Generate only 1 thumbnail initially for faster display
      const videoPromises = videoFiles.map(async (file) => {
        const id = uuid();
        const videoData = await generateThumbnailsFromVideo(file, 1); // Only 1 thumbnail
        const filename = file.name.replace(/\.[^/.]+$/, '');

        // Handle failed thumbnail generation — fallback to backend
        let thumbnails;
        let { thumbnailFailed } = videoData;
        if (videoData.thumbnails && videoData.thumbnails.length > 0) {
          thumbnails = videoData.thumbnails;
        } else {
          const backendThumbs = await callBackendThumbnailAPI(file);
          if (backendThumbs?.length) {
            thumbnails = backendThumbs;
            thumbnailFailed = false;
          } else {
            thumbnails = [ForNoImage];
          }
        }

        return {
          id,
          file,
          title: filename,
          description: '',
          visibility: '2', // Public
          selectedCategory:
            categories.length > 0
              ? { value: categories[0].id, name: categories[0].title }
              : { value: '', name: '' },
          selectedSubCategory: '',
          selectedPlaylist: '',
          selectedGroups: [],
          thumbnails,
          selectedThumbnail: 0,
          isGeneratingThumbnails: false,
          duration: videoData.duration || 0,
          size: file.size,
          width: videoData.width || 0,
          height: videoData.height || 0,
          thumbnailFailed: thumbnailFailed || false,
        };
      });

      const newVideos = await Promise.all(videoPromises);
      setVideos((prevVideos) => [...prevVideos, ...newVideos]);
      setIsLoadingVideos(false);

      // Generate remaining thumbnails in the background after list is shown
      setTimeout(async () => {
        setIsGeneratingThumbnails(true);
        const currentTotalVideos = videos.length + newVideos.length;

        // Mark all new videos as pending
        setPendingThumbnailGeneration((prev) => {
          const newSet = new Set(prev);
          newVideos.forEach((v) => newSet.add(v.id));
          return newSet;
        });

        // Generate thumbnails for each new video sequentially to show progress using reduce
        await newVideos.reduce(async (promise, video, i) => {
          await promise;

          // Remove from pending before starting generation
          setPendingThumbnailGeneration((prev) => {
            const newSet = new Set(prev);
            newSet.delete(video.id);
            return newSet;
          });

          await generateRemainingThumbnails(video, videos.length + i, currentTotalVideos);
        }, Promise.resolve());

        setIsGeneratingThumbnails(false);
        setPendingThumbnailGeneration((prev) => {
          const newSet = new Set(prev);
          newVideos.forEach((v) => newSet.delete(v.id));
          return newSet;
        });
      }, 100); // Small delay to ensure UI updates first

      // Clear the input
      if (additionalFileInputRef.current) {
        additionalFileInputRef.current.value = '';
      }
    },
    [categories, videos.length, userData, checkUploadLimit]
  );

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 MB';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${Math.round((bytes / 1024 ** i) * 100) / 100} ${sizes[i]}`;
  };

  // mm:ss for the badge in the thumbnail corner. Returns null when the probe
  // didn't yield a duration, so the badge is simply omitted rather than showing
  // a bogus "0:00".
  const formatDuration = (seconds) => {
    if (!seconds || !Number.isFinite(seconds)) return null;
    const total = Math.round(seconds);
    const mins = Math.floor(total / 60);
    const secs = total % 60;
    if (mins >= 60) {
      const hrs = Math.floor(mins / 60);
      return `${hrs}:${String(mins % 60).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const handleClose = useCallback(() => {
    // Clean up all blob URLs
    videos.forEach((video) => {
      if (video.thumbnails) {
        video.thumbnails.forEach((url) => {
          if (url && url.startsWith('blob:')) {
            URL.revokeObjectURL(url);
          }
        });
      }
    });
    setVideos([]);
    setExpandedVideos({});
    onClose();
  }, [videos, onClose]);

  // Helper functions
  const changeSize = (size) => {
    const newSize = size / 1024 / 1024;
    return newSize.toFixed(2);
  };

  const addVideoToAPI = async (draftID, draftCategoryState, secureUploadData = null) => {
    try {
      const apiUrl = `${baseUrl}/V6/addVideo`;
      const formData = new FormData();
      const draftData = draftCategoryState[draftID];

      if (draftData) {
        formData.append('draft_id', draftID || '');
        formData.append('title', draftData.title || '');
        formData.append('category_id', draftData.category || '');
        formData.append('file_name', draftData.fileName || '');

        // Check if this is a secure upload (has S3 keys) or traditional upload (has binary thumbnails)
        const isSecureUpload = secureUploadData && secureUploadData.s3Key;

        if (isSecureUpload) {
          // Secure upload mode: Video uploaded to S3, but thumbnails sent as binary
          console.log('[AddVideo] Using secure upload mode - video on S3, thumbnails as binary');

          formData.append('file_key', secureUploadData.s3Key);
          formData.append('document_id', secureUploadData.documentId || '');
          formData.append('bucket_folder_path', secureUploadData.s3Key);

          // Send thumbnails as binary (same as traditional mode)
          if (draftData.thumbnail) {
            try {
              const binaryThumbnail = base64ToBinary(draftData.thumbnail);
              formData.append('thumbnail', binaryThumbnail);
            } catch (error) {
              console.error('Main thumbnail conversion failed:', error.message);
            }
          }

          if (secureUploadData.thumbnails && Array.isArray(secureUploadData.thumbnails)) {
            secureUploadData.thumbnails.forEach((thumbnail) => {
              if (thumbnail) {
                try {
                  // Convert base64 to binary
                  const binaryThumbnail = typeof thumbnail === 'string'
                    ? base64ToBinary(thumbnail)
                    : thumbnail;
                  formData.append('thumbnails[]', binaryThumbnail);
                } catch (error) {
                  console.error('Thumbnail conversion failed:', error);
                }
              }
            });
          }
        } else {
          // Traditional upload mode: Use binary thumbnails
          console.log('[AddVideo] Using traditional upload mode with binary thumbnails');

          if (draftData.thumbnail) {
            try {
              const binaryThumbnail = base64ToBinary(draftData.thumbnail);
              formData.append('thumbnail', binaryThumbnail);
            } catch (error) {
              console.error('Main thumbnail conversion failed:', error.message);
            }
          }

          if (draftData.thumbnails && Array.isArray(draftData.thumbnails)) {
            draftData.thumbnails.forEach((binaryThumbnail) => {
              if (binaryThumbnail) {
                formData.append('thumbnails[]', binaryThumbnail);
              }
            });
          }

          formData.append('uploadKey', draftData.uploadKey || '');
          formData.append(
            'bucket_folder_path',
            `${draftData.s3Url}${draftData.uploadBucket}/${draftData.uploadKey}`
          );
          formData.append('s3_image_bucket_url', draftData.s3Url || '');
        }

        // Common fields for both modes
        formData.append('description', draftData.description || '');
        formData.append('video_size', changeSize(draftData.videoSize) || '');
        formData.append(
          'group_ids', (draftData.selectedGroups || []).join(',')
        );
        formData.append('visibility', draftData.visibility || '');
        formData.append(
          'subcategory_id',
          draftData.selectedSubCategory || ''
        );
        formData.append('playlist_ids', draftData.selectedPlaylist || '');
        formData.append('videoHeight', draftData.videoHeight || '');
        formData.append('videoWidth', draftData.videoWidth || '');
        formData.append('videoOreintation', draftData.orientation || '');
        formData.append('s3_status', draftData.uploadStatus || '');
      }

      const response = await axios.post(apiUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error) {
      console.error('Error adding video to API:', error.message || error);
      throw error;
    }
  };

  const updateDummy = (dummy, status = 0) => {
    if (status === 1) {
      dispatch(saveDummy({ [dummy?.draft_id]: dummy }));
    } else {
      dispatch(removeDummy(dummy));
    }
  };

  const uploadSingleVideo = async (video) => {
    const fileExtension = video.file.name.split('.').pop().toLowerCase();
    const number = Math.floor(Math.random() * 10000) + 1;
    const fileName = `${Date.now()}-${userId}-video-${number}.${fileExtension}`;
    const bucketName = getBucketName(userId);
    const s3Url = generateS3Url();
    const draftID = uuid();

    // Get main thumbnail (handle both normal thumbnails and ForNoImage case)
    let thumbnailBase64 = '';
    const selectedThumbnail = video.thumbnails[video.selectedThumbnail];

    if (
      selectedThumbnail === ForNoImage
      || video.selectedThumbnail === video.thumbnails.length
    ) {
      // User selected ForNoImage option (either ForNoImage path or index beyond array)
      // Convert ForNoImage to base64
      const response = await fetch(ForNoImage);
      const blob = await response.blob();
      thumbnailBase64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } else if (selectedThumbnail && selectedThumbnail.startsWith('data:')) {
      // Normal base64 thumbnail selected
      thumbnailBase64 = selectedThumbnail;
    }

    // Convert all thumbnails to binary in parallel (thumbnails are already base64)
    const thumbnailsBinary = await Promise.all(
      video.thumbnails.map(async (thumbnail, i) => {
        try {
          // Handle ForNoImage case (when thumbnail generation failed)
          if (thumbnail === ForNoImage) {
            // Convert ForNoImage to base64 then to binary
            const response = await fetch(ForNoImage);
            const blob = await response.blob();
            const base64 = await new Promise((resolve) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.readAsDataURL(blob);
            });
            return base64ToBinary(base64);
          }

          if (!thumbnail) {
            return null;
          }
          const binary = base64ToBinary(thumbnail);
          return binary;
        } catch (error) {
          console.error(`Error converting thumbnail ${i}:`, error.message);
          return null;
        }
      })
    );

    return new Promise((resolve, reject) => {
      uploadLargeFile({
        // Common parameters for both upload modes
        userId, // Required for secure upload (S3 key generation)
        bucket: bucketName,
        key: fileName,
        file: video.file,
        draftId: draftID,
        thumbnails: video.thumbnails, // Pass thumbnails for secure upload
        uploadPercentages,
        updateUploadPercentages,
        setUploadContainer,
        setUploadContainer2,
        setIamUploading,
        dummyData: {
          title: video.title,
          fileName: video.file.name, // Use original file name for display
          thumbnail: thumbnailBase64,
          thumbnails: thumbnailsBinary,
          category: video.selectedCategory.name,
          description: video.description,
          videoSize: video.size,
          selectedGroups: video.selectedGroups,
          changeSize,
        },
        updateDummy,
        setDraftCategoryState,
        onSuccess: async (draft_id, secureUploadData = null) => {
          // secureUploadData will be provided by secure upload manager
          // It contains: { s3Key, thumbnailKeys, documentId }
          const isSecureUpload = secureUploadData && secureUploadData.s3Key;

          console.log(`[Upload] onSuccess called for draft ${draft_id}`, {
            isSecureUpload,
            hasSecureData: !!secureUploadData,
          });

          const updatedDraftData = {
            title: video.title,
            category: video.selectedCategory.value,
            fileName: video.file?.name,
            thumbnail: thumbnailBase64,
            thumbnails: thumbnailsBinary, // Always use binary thumbnails
            description: video.description,
            videoSize: video.size,
            selectedGroups: video.selectedGroups,
            visibility: video.visibility,
            selectedSubCategory: video.selectedSubCategory,
            selectedPlaylist: video.selectedPlaylist,
            videoHeight: video.height,
            videoWidth: video.width,
            orientation: video.height < video.width ? 1 : 2,
            uploadStatus: 'completed',
            uploadKey: isSecureUpload ? secureUploadData.s3Key : fileName,
            uploadBucket: isSecureUpload ? '' : bucketName,
            s3Url: isSecureUpload ? '' : s3Url,
          };

          setDraftCategoryState((prevState) => ({
            ...prevState,
            [draft_id]: updatedDraftData,
          }));

          const tempDraftState = {
            [draft_id]: updatedDraftData,
          };

          try {
            // Add video to backend
            const response = await addVideoToAPI(draft_id, tempDraftState, secureUploadData);

            // If secure upload mode, link the draft to the document
            if (isSecureUpload && secureUploadData.documentId && response.draft_id) {
              console.log('[Upload] Linking draft to document:', {
                documentId: secureUploadData.documentId,
                draftId: response.draft_id,
              });

              await updateDraftLink({
                document_id: secureUploadData.documentId,
                draft_id: response.draft_id,
              });

              console.log('[Upload] Draft linked successfully');
            }

            resolve();
          } catch (error) {
            console.error('Failed to add video to API:', error);
            reject(error);
          }
        },
        onError: (error, draft_id) => {
          console.error('Upload failed for draft:', draft_id, error);
          reject(error);
        },
        updateUploadDetails,
      });
    });
  };

  // Toolbar handlers
  const handleClearAllTitles = useCallback(() => {
    setVideos((prevVideos) => prevVideos.map((video) => ({
      ...video,
      title: '',
    })));
  }, []);

  const handleClearAllDescriptions = useCallback(() => {
    setVideos((prevVideos) => prevVideos.map((video) => ({
      ...video,
      description: '',
    })));
  }, []);

  const handleApplyCategoryToAll = useCallback(() => {
    if (selectedCategoryForAll) {
      const selectedCat = categories.find(
        (cat) => cat.id === selectedCategoryForAll
      );
      if (selectedCat) {
        setVideos((prevVideos) => prevVideos.map((video) => ({
          ...video,
          selectedCategory: {
            value: selectedCat.id,
            name: selectedCat.title,
          },
        })));
        setApplyCategoryDialogOpen(false);
        setSelectedCategoryForAll('');
      }
    }
  }, [selectedCategoryForAll, categories]);

  const handleApplyVisibilityToAll = useCallback(() => {
    setVideos((prevVideos) => prevVideos.map((video) => ({
      ...video,
      visibility: selectedVisibilityForAll,
      // Clear groups if not group visibility
      selectedGroups:
        selectedVisibilityForAll === '3' ? video.selectedGroups : [],
    })));
    // If setting to group visibility, expand all videos to show group selection
    if (selectedVisibilityForAll === '3') {
      const expandedState = {};
      videos.forEach((video) => {
        expandedState[video.id] = true;
      });
      setExpandedVideos(expandedState);
    }
    setApplyVisibilityDialogOpen(false);
  }, [selectedVisibilityForAll, videos]);

  const handleResetAll = useCallback(() => {
    setVideos((prevVideos) => prevVideos.map((video) => {
      const filename = video.file.name.replace(/\.[^/.]+$/, '');
      return {
        ...video,
        title: filename,
        description: '',
        visibility: '2',
        selectedCategory:
          categories.length > 0
            ? { value: categories[0].id, name: categories[0].title }
            : { value: '', name: '' },
        selectedSubCategory: '',
        selectedPlaylist: '',
        selectedGroups: [],
      };
    }));
    setExpandedVideos({});
  }, [categories]);

  const handlePublishAll = async () => {
    if (videos.length === 0) return;

    // Validate group selection for videos with Group visibility
    const invalidVideos = videos.filter(
      (video) => video.visibility === '3'
        && (!video.selectedGroups || video.selectedGroups.length === 0)
    );

    if (invalidVideos.length > 0) {
      // Auto-expand videos that need group selection
      const expandedState = {};
      invalidVideos.forEach((video) => {
        expandedState[video.id] = true;
      });
      setExpandedVideos((prev) => ({
        ...prev,
        ...expandedState,
      }));

      alert(
        `Please select at least one group for the following videos with Group visibility:\n${invalidVideos
          .map((v) => `• ${v.title || 'Untitled Video'}`)
          .join('\n')}`
      );
      return;
    }

    setIsUploading(true);

    // Capture count BEFORE uploading to check if this is user's first upload
    const preUploadCount = userDetails?.totalContentUploaded || 0;

    // Close modal immediately after starting uploads
    handleClose();
    navigate(Pathname.yourVideos);

    try {
      // Upload all videos in parallel (or sequentially based on preference)
      const uploadPromises = videos.map((video) => uploadSingleVideo(video));
      await Promise.all(uploadPromises);

      // console.log('All videos uploaded successfully');

      // Navigate to ecosystem for new users (check pre-upload count, not post-upload)
      await autoSignIn();
      navigateToInviteFirstTime(preUploadCount);
    } catch (error) {
      console.error('Error uploading videos:', error);
    } finally {
      setIsUploading(false);
    }
  };
  const sameToAllowed = userData?.totalContentUploaded + videos.length === ALLOWED_UPLOADS_COUNT;

  // ---------------------------------------------------------------------------
  // Derived batch summary. The header, the toolbar and the footer all read from
  // these, so the numbers on screen can never disagree with each other.
  // ---------------------------------------------------------------------------
  const totalBytes = videos.reduce((sum, v) => sum + (v.size || 0), 0);
  const missingTitleCount = videos.filter((v) => !v.title.trim()).length;
  const missingGroupsCount = videos.filter(
    (v) => v.visibility === '3' && (!v.selectedGroups || v.selectedGroups.length === 0)
  ).length;
  const publishBlocked = Boolean(
    isUploading
    || gettingCategories
    || currentLimitError
    || missingTitleCount
    || missingGroupsCount
  );
  // Publish used to just go grey with no explanation. Say WHY it's unavailable,
  // in the footer, right next to the button.
  const blockingHint = (() => {
    if (missingTitleCount) {
      return missingTitleCount === 1
        ? '1 video still needs a title'
        : `${missingTitleCount} videos still need a title`;
    }
    if (missingGroupsCount) {
      return missingGroupsCount === 1
        ? '1 video set to Groups needs at least one group'
        : `${missingGroupsCount} videos set to Groups need at least one group`;
    }
    return null;
  })();
  const defaultDestination = categories[0]?.title || 'All Videos';
  const canAddMore = !currentLimitError && !sameToAllowed;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      aria-labelledby="mvu-heading"
      PaperProps={{
        className: 'mvu-root',
        style: {
          width: '100%',
          maxWidth: 1180,
          maxHeight: '92vh',
          borderRadius: 20,
          overflow: 'hidden',
          boxShadow: '0 28px 70px rgba(15,17,28,0.22)',
        },
      }}
    >
      {/* One global stylesheet for the whole modal. It is global on purpose:
          MUI portals its menus and the nested dialogs to <body>, and `.mvu-menu`
          / `.mvu-root` still have to reach them there. */}
      <style>{MVU_STYLES}</style>

      {/* overflow hidden + minHeight 0 hand the scroll to .mvu-body, so the
          header, toolbar and footer stay put while only the card list moves. */}
      <DialogContent
        style={{
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <div className="mvu-shell">

          {/* ─── header ─────────────────────────────────────────────────── */}
          <header className="mvu-header">
            <span className="mvu-header-icon">
              <IconFilm size={21} className="mvu-film" />
            </span>
            <div className="mvu-header-text">
              <h2 className="mvu-title" id="mvu-heading">Review &amp; publish</h2>
              <p className="mvu-subtitle">
                {isLoadingVideos ? (
                  <span>Getting your selection ready…</span>
                ) : (
                  <>
                    <span>
                      {videos.length}
                      {videos.length === 1 ? ' video' : ' videos'}
                    </span>
                    {totalBytes > 0 && (
                      <>
                        <span className="mvu-dot" aria-hidden="true" />
                        <span>{formatFileSize(totalBytes)}</span>
                      </>
                    )}
                    <span className="mvu-dot" aria-hidden="true" />
                    <span>
                      Publishing to
                      {' '}
                      <strong>{defaultDestination}</strong>
                      {' '}
                      unless you pick a category
                    </span>
                  </>
                )}
              </p>
            </div>
            <div className="mvu-header-actions">
              {isGeneratingThumbnails && (
                <span className="mvu-chip">
                  <IconSparkles size={13} className="mvu-sparkle" />
                  Generating thumbnails…
                </span>
              )}
              {/* Lives outside the videos.length guard so the empty state's own
                  "Choose videos" button can trigger it too. */}
              <input
                ref={additionalFileInputRef}
                type="file"
                accept=".mp4,.mov,.mpg,.m4v,.m2ts,.hevc,.h264"
                multiple
                onChange={handleAddMoreVideos}
                style={{ display: 'none' }}
              />
              {videos.length > 0 && (
                <button
                  type="button"
                  className="mvu-btn mvu-btn-ghost mvu-btn-sm"
                  onClick={() => additionalFileInputRef.current?.click()}
                  disabled={!canAddMore}
                  title={canAddMore ? 'Add more videos to this batch' : 'You have reached your upload limit'}
                >
                  <IconPlus size={15} className="mvu-plus" />
                  Add more
                </button>
              )}
              <button
                type="button"
                className="mvu-iconbtn"
                onClick={handleClose}
                disabled={isUploading}
                aria-label="Close without publishing"
                title="Close"
              >
                <IconX size={18} className="mvu-x" />
              </button>
            </div>
          </header>

          {/* ─── bulk actions ───────────────────────────────────────────────
              Was five unlabelled icon buttons buried in the footer; now a
              labelled strip at the top, where batch-wide actions belong. */}
          {videos.length > 0 && !isLoadingVideos && (
            <div className="mvu-toolbar">
              <span className="mvu-toolbar-label">Apply to all</span>
              <button
                type="button"
                className="mvu-btn mvu-btn-quiet mvu-btn-sm"
                onClick={() => setApplyCategoryDialogOpen(true)}
              >
                <IconFolder size={15} />
                Category
              </button>
              <button
                type="button"
                className="mvu-btn mvu-btn-quiet mvu-btn-sm"
                onClick={() => setApplyVisibilityDialogOpen(true)}
              >
                <IconGlobe size={15} />
                Visibility
              </button>
              <span className="mvu-toolbar-sep" aria-hidden="true" />
              <button
                type="button"
                className="mvu-btn mvu-btn-quiet mvu-btn-sm"
                onClick={handleClearAllTitles}
              >
                <IconType size={15} />
                Clear titles
              </button>
              <button
                type="button"
                className="mvu-btn mvu-btn-quiet mvu-btn-sm"
                onClick={handleClearAllDescriptions}
              >
                <IconAlignLeft size={15} />
                Clear descriptions
              </button>
              <button
                type="button"
                className="mvu-btn mvu-btn-quiet mvu-btn-sm"
                onClick={handleResetAll}
              >
                <IconRotate size={15} className="mvu-rotate" />
                Reset all
              </button>
              <span className="mvu-spacer" />
              <span className="mvu-meta">
                <IconHardDrive size={14} />
                {formatFileSize(totalBytes)}
              </span>
            </div>
          )}

          {/* ─── upload-limit banner ────────────────────────────────────── */}
          {currentLimitError && !isLoadingVideos && (
            <div className="mvu-banner" role="alert">
              <span className="mvu-banner-icon">
                <IconAlert size={18} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h4>Upload limit reached</h4>
                <p>{currentLimitError.message}</p>
              </div>
              <button
                type="button"
                className="mvu-btn mvu-btn-danger mvu-btn-sm"
                onClick={() => {
                  navigate(userAccessedData ? '/affiliate' : Pathname.plans);
                  handleClose();
                }}
              >
                Upgrade plan
              </button>
            </div>
          )}

          {/* ─── body ───────────────────────────────────────────────────── */}
          <div className="mvu-body">
            {isLoadingVideos ? (
              <>
                {/* Skeletons in the shape of the real cards, rather than a lone
                    spinner on an empty sheet — the wait reads as progress and
                    the layout doesn't jump when the cards arrive. */}
                <div className="mvu-state" style={{ flex: 'none', padding: '18px 24px 4px' }}>
                  <span className="mvu-state-icon">
                    <span className="mvu-spin mvu-spin-lg" style={{ color: V2.slate }} />
                  </span>
                  <p className="mvu-state-title">
                    Preparing
                    {' '}
                    {initialFiles.length || videos.length}
                    {(initialFiles.length || videos.length) === 1 ? ' video' : ' videos'}
                  </p>
                  <p className="mvu-state-sub">
                    Reading each file, grabbing a frame for the cover image and filling in
                    the details. This only takes a moment.
                  </p>
                </div>
                {[0, 1, 2].map((row) => (
                  <div className="mvu-skel-card" key={row} aria-hidden="true">
                    <div className="mvu-skel" style={{ flex: 'none', width: 200, height: 113, borderRadius: 13 }} />
                    <div className="mvu-skel-fields">
                      <div className="mvu-skel" style={{ width: '32%', height: 11 }} />
                      <div className="mvu-skel" style={{ height: 40 }} />
                      <div style={{ display: 'flex', gap: 12 }}>
                        <div className="mvu-skel" style={{ flex: 1, height: 38 }} />
                        <div className="mvu-skel" style={{ flex: 'none', width: 210, height: 38 }} />
                      </div>
                    </div>
                  </div>
                ))}
              </>
            ) : videos.length === 0 ? (
              <div className="mvu-state">
                <span className="mvu-state-icon">
                  <IconFilm size={28} />
                </span>
                <p className="mvu-state-title">Nothing to review yet</p>
                <p className="mvu-state-sub">
                  Pick a few video files and they&apos;ll appear here, ready to title,
                  organise and publish together.
                </p>
                <button
                  type="button"
                  className="mvu-btn mvu-btn-primary"
                  style={{ marginTop: 10 }}
                  onClick={() => additionalFileInputRef.current?.click()}
                >
                  <IconUpload size={16} className="mvu-uparrow" />
                  Choose videos
                </button>
              </div>
            ) : (
              videos.map((video, index) => {
                const isExpanded = Boolean(expandedVideos[video.id]);
                const needsGroups = video.visibility === '3'
                  && (!video.selectedGroups || video.selectedGroups.length === 0);
                const progress = thumbnailProgress[video.id];
                const thumbBusy = Boolean(progress) || pendingThumbnailGeneration.has(video.id);
                const hasThumbs = Boolean(video.thumbnails && video.thumbnails.length > 0);
                const placeholderPicked = hasThumbs
                  && video.selectedThumbnail === video.thumbnails.length;
                const coverSrc = hasThumbs
                  ? (placeholderPicked ? ForNoImage : video.thumbnails[video.selectedThumbnail])
                  : null;
                const duration = formatDuration(video.duration);

                return (
                  <article
                    key={video.id}
                    className={`mvu-card${needsGroups ? ' mvu-card-warn' : ''}`}
                  >
                    <div className="mvu-card-main">
                      {/* cover */}
                      <div className="mvu-thumb">
                        {coverSrc && <img src={coverSrc} alt="" />}
                        <span className="mvu-thumb-index">{index + 1}</span>
                        {duration && !thumbBusy && (
                          <span className="mvu-thumb-dur">{duration}</span>
                        )}
                        {(!hasThumbs || thumbBusy) && (
                          <div className="mvu-thumb-veil">
                            <span className="mvu-spin" />
                            <span>
                              {progress
                                ? `${progress.current}/${progress.total} frames`
                                : 'Preparing'}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* primary fields */}
                      <div className="mvu-fields">
                        <div>
                          <label className="mvu-label" htmlFor={`mvu-title-${video.id}`}>
                            Title
                          </label>
                          <TextField
                            id={`mvu-title-${video.id}`}
                            value={video.title}
                            onChange={(e) => updateVideo(video.id, { title: e.target.value })}
                            placeholder="Give this video a title"
                            variant="outlined"
                            size="small"
                            fullWidth
                            className="mvu-titlefield"
                          />
                          <span className="mvu-filename" title={video.file?.name}>
                            {video.file?.name}
                          </span>
                        </div>

                        <div className="mvu-row">
                          <div>
                            <label className="mvu-label" htmlFor={`mvu-category-${video.id}`}>
                              Category
                            </label>
                            {gettingCategories ? (
                              <div className="mvu-meta" style={{ height: 40 }}>
                                <span className="mvu-spin" />
                                Loading categories…
                              </div>
                            ) : (
                              <FormControl fullWidth size="small" variant="outlined">
                                <Select
                                  id={`mvu-category-${video.id}`}
                                  value={video.selectedCategory.value}
                                  disabled={categories.length === 0}
                                  MenuProps={MENU_PROPS}
                                  displayEmpty
                                  renderValue={(value) => {
                                    const match = categories.find((cat) => cat.id === value);
                                    return (
                                      <>
                                        <IconFolder size={15} style={{ flex: 'none', color: V2.slate }} />
                                        <span>{match?.title || defaultDestination}</span>
                                      </>
                                    );
                                  }}
                                  onChange={(e) => {
                                    if (e.target.value === '__create_new__') {
                                      pendingVideoIdRef.current = video.id;
                                      setPendingVideoIdForCategory(video.id);
                                      setShowCreateCategoryModal(true);
                                      return;
                                    }
                                    const selectedCat = categories.find(
                                      (cat) => cat.id === e.target.value
                                    );
                                    updateVideo(video.id, {
                                      selectedCategory: {
                                        value: e.target.value,
                                        name: selectedCat?.title || '',
                                      },
                                    });
                                  }}
                                >
                                  <MenuItem value="__create_new__">
                                    <IconPlus size={15} style={{ flex: 'none', marginRight: 8 }} />
                                    <span style={{ fontWeight: 700 }}>New category</span>
                                  </MenuItem>
                                  <Divider />
                                  {categories.map((cat) => (
                                    <MenuItem key={cat.id} value={cat.id}>
                                      <IconFolder size={15} style={{ flex: 'none', marginRight: 8, color: V2.slate }} />
                                      {cat.title}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )}
                          </div>

                          <div>
                            <span className="mvu-label">Visibility</span>
                            {/* Segmented, not the old click-to-cycle chip: all
                                three states are visible and one tap away. */}
                            <div
                              className="mvu-seg"
                              role="group"
                              aria-label={`Visibility for ${video.title || `video ${index + 1}`}`}
                            >
                              {VISIBILITY_OPTIONS.map(({ value, label, desc, Icon }) => (
                                <button
                                  key={value}
                                  type="button"
                                  title={desc}
                                  aria-pressed={video.visibility === value}
                                  onClick={() => {
                                    updateVideo(video.id, {
                                      visibility: value,
                                      // Dropping out of Groups clears the picks so a
                                      // stale selection can't be published later.
                                      selectedGroups: value === '3' ? video.selectedGroups : [],
                                    });
                                    if (value === '3') {
                                      setExpandedVideos((prev) => ({ ...prev, [video.id]: true }));
                                    }
                                  }}
                                >
                                  <Icon size={14} />
                                  {label}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>

                        {needsGroups && (
                          <div className="mvu-note">
                            <IconAlert size={15} style={{ flex: 'none', marginTop: 1 }} />
                            <span>Pick at least one group below before publishing this video.</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* card footer */}
                    <div className="mvu-card-foot">
                      <button
                        type="button"
                        className="mvu-btn mvu-btn-quiet mvu-btn-sm"
                        onClick={() => toggleExpanded(video.id)}
                        aria-expanded={isExpanded}
                        aria-controls={`mvu-more-${video.id}`}
                      >
                        <IconSliders size={15} className="mvu-sliders" />
                        {isExpanded ? 'Hide options' : 'More options'}
                        {isExpanded
                          ? <IconChevronUp size={14} className="mvu-chev" />
                          : <IconChevronDown size={14} className="mvu-chev" />}
                      </button>
                      <span className="mvu-spacer" />
                      <span className="mvu-meta">
                        <IconHardDrive size={13} />
                        {formatFileSize(video.size)}
                      </span>
                      <button
                        type="button"
                        className="mvu-iconbtn mvu-iconbtn-danger"
                        onClick={() => removeVideo(video.id)}
                        aria-label={`Remove ${video.title || `video ${index + 1}`} from this upload`}
                        title="Remove from this upload"
                      >
                        <IconTrash size={17} className="mvu-trash" />
                      </button>
                    </div>

                    {/* expanded options — inside the card, on a recessed well */}
                    <Collapse in={isExpanded} timeout={280} unmountOnExit>
                      <div className="mvu-more" id={`mvu-more-${video.id}`}>
                        {hasThumbs && (
                          <section>
                            <div className="mvu-sect-head">
                              <span className="mvu-sect-icon"><IconImage size={15} /></span>
                              <span className="mvu-sect-title">Cover image</span>
                            </div>
                            <div className="mvu-strip">
                              {video.thumbnails.map((thumb, thumbIndex) => (
                                <button
                                  key={thumbIndex}
                                  type="button"
                                  className="mvu-thumbopt"
                                  aria-pressed={video.selectedThumbnail === thumbIndex}
                                  aria-label={`Use frame ${thumbIndex + 1} as the cover`}
                                  onClick={() => updateVideo(video.id, { selectedThumbnail: thumbIndex })}
                                >
                                  <img src={thumb} alt="" />
                                  {video.selectedThumbnail === thumbIndex && (
                                    <span className="mvu-thumbopt-check">
                                      <IconCheck size={11} />
                                    </span>
                                  )}
                                </button>
                              ))}
                              <button
                                type="button"
                                className="mvu-thumbopt"
                                aria-pressed={placeholderPicked}
                                aria-label="Use the default placeholder as the cover"
                                onClick={() => updateVideo(video.id, {
                                  selectedThumbnail: video.thumbnails.length,
                                })}
                              >
                                <img src={ForNoImage} alt="" />
                                {placeholderPicked && (
                                  <span className="mvu-thumbopt-check">
                                    <IconCheck size={11} />
                                  </span>
                                )}
                              </button>
                            </div>
                          </section>
                        )}

                        <section>
                          <div className="mvu-sect-head">
                            <span className="mvu-sect-icon"><IconSliders size={15} /></span>
                            <span className="mvu-sect-title">Details</span>
                          </div>
                          <div className="mvu-more-grid">
                            <div>
                              <label className="mvu-label" htmlFor={`mvu-desc-${video.id}`}>
                                Description
                              </label>
                              <TextField
                                id={`mvu-desc-${video.id}`}
                                value={video.description}
                                onChange={(e) => updateVideo(video.id, { description: e.target.value })}
                                placeholder="Add a short description…"
                                variant="outlined"
                                size="small"
                                fullWidth
                              />
                            </div>

                            <div>
                              <label className="mvu-label" htmlFor={`mvu-subcat-${video.id}`}>
                                Subcategory
                              </label>
                              {gettingSubCategories ? (
                                <div className="mvu-meta" style={{ height: 40 }}>
                                  <span className="mvu-spin" />
                                  Loading…
                                </div>
                              ) : (
                                <FormControl fullWidth size="small" variant="outlined">
                                  <Select
                                    id={`mvu-subcat-${video.id}`}
                                    value={video.selectedSubCategory}
                                    MenuProps={MENU_PROPS}
                                    displayEmpty
                                    renderValue={(value) => {
                                      const match = subCategories.find((sub) => sub.id === value);
                                      return match ? (
                                        <>
                                          <IconFolderTree size={15} style={{ flex: 'none', color: V2.slate }} />
                                          <span>{match.title}</span>
                                        </>
                                      ) : (
                                        <span style={{ color: V2.faint }}>None</span>
                                      );
                                    }}
                                    onChange={(e) => {
                                      if (e.target.value === '__create_new__') {
                                        setPendingVideoIdForSubCategory(video.id);
                                        setShowAddSubCategoryModal(true);
                                        return;
                                      }
                                      updateVideo(video.id, { selectedSubCategory: e.target.value });
                                    }}
                                  >
                                    <MenuItem value="__create_new__">
                                      <IconPlus size={15} style={{ flex: 'none', marginRight: 8 }} />
                                      <span style={{ fontWeight: 700 }}>New subcategory</span>
                                    </MenuItem>
                                    <Divider />
                                    <MenuItem value="">
                                      <span style={{ color: V2.sub }}>None</span>
                                    </MenuItem>
                                    {subCategories.map((subcat) => (
                                      <MenuItem key={subcat.id} value={subcat.id}>
                                        <IconFolderTree size={15} style={{ flex: 'none', marginRight: 8, color: V2.slate }} />
                                        {subcat.title}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              )}
                            </div>

                            <div>
                              <label className="mvu-label" htmlFor={`mvu-playlist-${video.id}`}>
                                Playlist
                              </label>
                              {gettingPlaylists ? (
                                <div className="mvu-meta" style={{ height: 40 }}>
                                  <span className="mvu-spin" />
                                  Loading…
                                </div>
                              ) : (
                                <FormControl fullWidth size="small" variant="outlined">
                                  <Select
                                    id={`mvu-playlist-${video.id}`}
                                    value={video.selectedPlaylist}
                                    MenuProps={MENU_PROPS}
                                    displayEmpty
                                    renderValue={(value) => {
                                      const match = playlists.find((pl) => pl.id === value);
                                      return match ? (
                                        <>
                                          <IconListMusic size={15} style={{ flex: 'none', color: V2.slate }} />
                                          <span>{match.title}</span>
                                        </>
                                      ) : (
                                        <span style={{ color: V2.faint }}>None</span>
                                      );
                                    }}
                                    onChange={(e) => {
                                      if (e.target.value === '__create_new__') {
                                        setPendingVideoIdForPlaylist(video.id);
                                        setShowAddPlaylistModal(true);
                                        return;
                                      }
                                      updateVideo(video.id, { selectedPlaylist: e.target.value });
                                    }}
                                  >
                                    <MenuItem value="__create_new__">
                                      <IconPlus size={15} style={{ flex: 'none', marginRight: 8 }} />
                                      <span style={{ fontWeight: 700 }}>New playlist</span>
                                    </MenuItem>
                                    <Divider />
                                    <MenuItem value="">
                                      <span style={{ color: V2.sub }}>None</span>
                                    </MenuItem>
                                    {playlists.map((playlist) => (
                                      <MenuItem key={playlist.id} value={playlist.id}>
                                        <IconListMusic size={15} style={{ flex: 'none', marginRight: 8, color: V2.slate }} />
                                        {playlist.title}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              )}
                            </div>
                          </div>
                        </section>

                        {video.visibility === '3' && (
                          <section>
                            <div className="mvu-sect-head">
                              <span className="mvu-sect-icon"><IconUsers size={15} /></span>
                              <span className="mvu-sect-title">Who can see it</span>
                              {video.selectedGroups?.length > 0 && (
                                <span className="mvu-chip" style={{ height: 24, fontSize: 11 }}>
                                  {video.selectedGroups.length}
                                  {' '}
                                  selected
                                </span>
                              )}
                            </div>
                            {!groupsLoaded ? (
                              <div className="mvu-meta">
                                <span className="mvu-spin" />
                                Loading groups…
                              </div>
                            ) : groups.length === 0 ? (
                              <div className="mvu-note">
                                <IconAlert size={15} style={{ flex: 'none', marginTop: 1 }} />
                                <span>
                                  You don&apos;t have any groups yet. Create one first, or set this
                                  video to All Viewers or Private.
                                </span>
                              </div>
                            ) : (
                              // Toggle pills rather than a checkbox column: they wrap
                              // to any width and read as a set of choices.
                              <div className="mvu-groups">
                                {groups.map((group) => {
                                  const picked = video.selectedGroups.includes(group.id);
                                  return (
                                    <button
                                      key={group.id}
                                      type="button"
                                      className="mvu-groupchip"
                                      aria-pressed={picked}
                                      onClick={() => updateVideo(video.id, {
                                        selectedGroups: picked
                                          ? video.selectedGroups.filter((id) => id !== group.id)
                                          : [...video.selectedGroups, group.id],
                                      })}
                                    >
                                      {picked ? <IconCheck size={14} /> : <IconUsers size={14} />}
                                      {group.title}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </section>
                        )}
                      </div>
                    </Collapse>
                  </article>
                );
              })
            )}
          </div>

          {/* ─── footer ─────────────────────────────────────────────────── */}
          {videos.length > 0 && !isLoadingVideos && (
            <footer className="mvu-footer">
              <div className="mvu-footer-info">
                <div className="mvu-footer-count">
                  <IconFilm size={15} style={{ color: V2.sub }} />
                  <span>
                    {videos.length}
                    {videos.length === 1 ? ' video' : ' videos'}
                    {' '}
                    ready
                  </span>
                  <span className="mvu-dot" aria-hidden="true" />
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: V2.sub }}>
                    {formatFileSize(totalBytes)}
                  </span>
                </div>
                {blockingHint && (
                  <p className="mvu-footer-hint">
                    <IconAlert size={13} />
                    {blockingHint}
                  </p>
                )}
              </div>
              <div className="mvu-footer-actions">
                <button
                  type="button"
                  className="mvu-btn mvu-btn-ghost"
                  onClick={handleClose}
                  disabled={isUploading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="mvu-btn mvu-btn-primary"
                  onClick={handlePublishAll}
                  disabled={publishBlocked}
                >
                  {isUploading ? (
                    <>
                      <span className="mvu-spin" />
                      Starting upload…
                    </>
                  ) : gettingCategories ? (
                    <>
                      <span className="mvu-spin" />
                      Loading categories…
                    </>
                  ) : (
                    <>
                      <IconUpload size={16} className="mvu-uparrow" />
                      Publish
                      {' '}
                      {videos.length}
                      {videos.length === 1 ? ' video' : ' videos'}
                    </>
                  )}
                </button>
              </div>
            </footer>
          )}
        </div>
      </DialogContent>

      {/* ─── apply one category to the whole batch ─────────────────────────
          No <style> here: MVU_STYLES above is global, so `.mvu-root` still
          reaches these papers even though MUI portals them to <body>. */}
      <Dialog
        open={applyCategoryDialogOpen}
        onClose={() => setApplyCategoryDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          className: 'mvu-root',
          style: {
            maxWidth: 440,
            borderRadius: 20,
            boxShadow: '0 28px 70px rgba(15,17,28,0.22)',
          },
        }}
      >
        <div className="mvu-sheet">
          <div className="mvu-sheet-head">
            <span className="mvu-sheet-icon"><IconFolder size={18} /></span>
            <div style={{ minWidth: 0 }}>
              <h3 className="mvu-sheet-title">Set one category</h3>
              <p className="mvu-sheet-sub">
                Applies to all
                {' '}
                {videos.length}
                {videos.length === 1 ? ' video' : ' videos'}
                {' '}
                in this batch.
              </p>
            </div>
          </div>
          <FormControl fullWidth size="small" variant="outlined">
            <Select
              value={selectedCategoryForAll}
              MenuProps={MENU_PROPS}
              displayEmpty
              renderValue={(value) => {
                const match = categories.find((cat) => cat.id === value);
                return match ? (
                  <>
                    <IconFolder size={15} style={{ flex: 'none', color: V2.slate }} />
                    <span>{match.title}</span>
                  </>
                ) : (
                  <span style={{ color: V2.faint }}>Choose a category…</span>
                );
              }}
              onChange={(e) => {
                if (e.target.value === '__create_new__') {
                  pendingVideoIdRef.current = '__all__';
                  setPendingVideoIdForCategory('__all__');
                  setShowCreateCategoryModal(true);
                  setApplyCategoryDialogOpen(false);
                  return;
                }
                setSelectedCategoryForAll(e.target.value);
              }}
            >
              <MenuItem value="__create_new__">
                <IconPlus size={15} style={{ flex: 'none', marginRight: 8 }} />
                <span style={{ fontWeight: 700 }}>New category</span>
              </MenuItem>
              <Divider />
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  <IconFolder size={15} style={{ flex: 'none', marginRight: 8, color: V2.slate }} />
                  {cat.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <div className="mvu-sheet-actions">
            <button
              type="button"
              className="mvu-btn mvu-btn-quiet"
              onClick={() => {
                setApplyCategoryDialogOpen(false);
                setSelectedCategoryForAll('');
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="mvu-btn mvu-btn-primary"
              onClick={handleApplyCategoryToAll}
              disabled={!selectedCategoryForAll}
            >
              <IconCheck size={16} />
              Apply
            </button>
          </div>
        </div>
      </Dialog>

      {/* ─── apply one visibility to the whole batch ─────────────────────── */}
      <Dialog
        open={applyVisibilityDialogOpen}
        onClose={() => setApplyVisibilityDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          className: 'mvu-root',
          style: {
            maxWidth: 460,
            borderRadius: 20,
            boxShadow: '0 28px 70px rgba(15,17,28,0.22)',
          },
        }}
      >
        <div className="mvu-sheet">
          <div className="mvu-sheet-head">
            <span className="mvu-sheet-icon"><IconGlobe size={18} /></span>
            <div style={{ minWidth: 0 }}>
              <h3 className="mvu-sheet-title">Set one visibility</h3>
              <p className="mvu-sheet-sub">
                Applies to all
                {' '}
                {videos.length}
                {videos.length === 1 ? ' video' : ' videos'}
                {' '}
                in this batch.
              </p>
            </div>
          </div>
          {/* Selectable cards instead of a dropdown: three options with a line
              of explanation each, all visible at once. */}
          <div className="mvu-optcards">
            {VISIBILITY_OPTIONS.map(({ value, label, desc, Icon }) => (
              <button
                key={value}
                type="button"
                className="mvu-optcard"
                aria-pressed={selectedVisibilityForAll === value}
                onClick={() => setSelectedVisibilityForAll(value)}
              >
                <span className="mvu-optcard-icon"><Icon size={17} /></span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span className="mvu-optcard-name">{label}</span>
                  <span className="mvu-optcard-desc">{desc}</span>
                </span>
                {selectedVisibilityForAll === value && (
                  <span className="mvu-optcard-tick">
                    <IconCheck size={12} className="mvu-tick" />
                  </span>
                )}
              </button>
            ))}
          </div>
          {selectedVisibilityForAll === '3' && (
            <div className="mvu-note" style={{ marginTop: 12 }}>
              <IconAlert size={15} style={{ flex: 'none', marginTop: 1 }} />
              <span>
                Every video will need at least one group picked before you can publish —
                they&apos;ll open automatically so you can choose.
              </span>
            </div>
          )}
          <div className="mvu-sheet-actions">
            <button
              type="button"
              className="mvu-btn mvu-btn-quiet"
              onClick={() => setApplyVisibilityDialogOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="mvu-btn mvu-btn-primary"
              onClick={handleApplyVisibilityToAll}
            >
              <IconCheck size={16} />
              Apply
            </button>
          </div>
        </div>
      </Dialog>

      {/* Create Category Modal */}
      {showCreateCategoryModal && (
        <CreateCategoryPage
          initialOpen={showCreateCategoryModal}
          onModalClose={() => {
            // Don't reset pendingVideoIdRef here - handleCategoryCreated will handle it
            // onModalClose is called BEFORE onCategoryCreated due to useEffect in CreateCategory
            // So we must preserve the ref value for handleCategoryCreated to use
            setShowCreateCategoryModal(false);
          }}
          onCategoryCreated={handleCategoryCreated}
        />
      )}
    </Dialog>
  );
};

const MultipleVideoUploadModal = (props) => (isNewUIEnabled()
  ? <MultipleVideoUploadModalNew {...props} />
  : <MultipleVideoUploadModalClassic {...props} />);

export default MultipleVideoUploadModal;
